import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import DisambiguationPanel from '../components/DisambiguationPanel';
import CorporateStructurePanel from '../components/CorporateStructurePanel';
import CSLAnnotatedStructure from '../components/CSLAnnotatedStructure';
import CorporateStructureSection from '../components/CorporateStructureSection';
import './SearchPage.css';

// Configure axios to send credentials (cookies) with requests
axios.defaults.withCredentials = true;

interface SearchResult {
  name: string;
  source: string;
  type: string;
  programs?: string[];
  addresses?: Array<{ country: string; address: string }>;
  score?: number;
  name_similarity?: number;
  effective_score?: number;
  federal_register_notice?: string;
  remarks?: string;
  license_requirement?: string;
  license_policy?: string;
  standard_order?: string;
  alt_names?: string[];
  searched_entity?: string;
  search_source?: string;
}

interface SearchResponse {
  success: boolean;
  total: number;
  results: SearchResult[];
  filtered_results?: SearchResult[];
  weak_matches_filtered?: number;
  filter_summary?: Record<string, number>;
  error?: string;
  query_url?: string;
  query_params?: Record<string, string>;
  enhanced_search?: boolean;
  search_queries?: Array<{
    name: string;
    type: string;
    results_count: number;
  }>;
}

interface ScreeningListAnalysis {
  list_name?: string;
  is_same_entity?: boolean | null;
  confidence?: string;
  reasoning?: string;
  listing_reason?: string;
  listing_date?: string;
  restrictions?: string;
  federal_register_url?: string;
}

interface PRCVerification {
  mainland_registration?: {
    verified?: boolean | null;
    registration_type?: string;
    registration_number?: string;
    registered_name?: string;
    registration_authority?: string;
  };
  hong_kong_entity?: {
    exists?: boolean | null;
    company_number?: string;
    note?: string;
  };
  state_ownership?: {
    is_soe?: boolean | null;
    evidence?: string;
  };
  military_connections?: {
    has_connections?: boolean | null;
    details?: string;
  };
}

interface KeySource {
  name: string;
  url?: string;
}

interface AIResearch {
  company_overview?: string;
  // New investigative format fields
  public_sources_confirm?: string[];
  public_sources_do_not_confirm?: string[];
  screening_list_analysis?: ScreeningListAnalysis[];
  prc_verification?: PRCVerification;
  risk_indicators?: string[];
  key_sources?: (string | KeySource)[];
  grounding_source_urls?: Array<{ title: string; url: string }>;
  // Legacy fields (for backward compatibility)
  export_compliance_concerns?: string[];
  government_military_affiliations?: string[];
  prc_registration_status?: {
    has_legitimate_registration?: boolean | null;
    registration_details?: string;
    state_owned_enterprise?: boolean | null;
    ccp_affiliations?: string;
  };
  sources_consulted?: string[];
  alternate_language_names?: string[];
  // Common fields
  research_confidence?: string;
  ai_provider?: string;
  has_china_connection?: boolean;
}

interface EnrichedProfile {
  original_name: string;
  original_domain?: string;
  original_country?: string;
  legal_name?: string;
  trade_names?: string[];
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  full_address?: string;
  industry?: string;
  description?: string;
  parent_company?: string;
  enrichment_confidence: string;
  enrichment_sources?: string[];
  enrichment_source_urls?: Array<{ title: string; url: string }>;
  is_enriched: boolean;
}

interface EnrichmentResult {
  success: boolean;
  profile?: EnrichedProfile;
  is_enriched: boolean;
  error?: string;
}

interface OpenSanctionsResult {
  id: string;
  caption: string;
  schema: string;
  score: number;
  names: string[];
  countries: string[];
  datasets: string[];
  topics: string[];
  is_sanctioned: boolean;
  list_sources: string[];
  match_category?: string;
  match_explanation?: string;
  entity_url?: string;
}

interface OpenSanctionsResponse {
  success: boolean;
  enabled: boolean;
  selected: boolean;
  total?: number;
  results?: OpenSanctionsResult[];
  sanctioned_count?: number;
  high_risk_count?: number;
  medium_risk_count?: number;
  low_risk_count?: number;
  informational_count?: number;
  risk_level?: string;
  list_sources?: string[];
  summary?: string;
  error?: string;
}

interface MatchValidation {
  match_index: number;
  match_name: string;
  is_same_entity: boolean | null;
  relationship: 'EXACT' | 'SUBSIDIARY' | 'AFFILIATED' | 'COINCIDENTAL' | 'UNKNOWN';
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  reasoning: string;
  rechecked?: boolean;
  recheck_source_url?: string;
  recheck_source_title?: string;
}

interface MatchValidationResult {
  success: boolean;
  validations: MatchValidation[];
  summary: {
    total_matches: number;
    confirmed_count: number;
    uncertain_count: number;
    coincidental_count: number;
    unvalidated_count?: number;
  };
  ai_provider?: string;
  skipped?: boolean;
  skip_reason?: string;
  partial_validation?: boolean;
  partial_reason?: string;
  error?: string;
  prompt_sent?: string;
  name_evidence_summary?: {
    signals_found: number;
    matches_with_evidence?: string[];
    SCREENED_NAME_IN_ALT_NAMES?: number;
    SCREENED_NAME_IN_REMARKS?: number;
    NEAR_IDENTICAL_NAME?: number;
    ACRONYM_MATCH?: number;
    POSSIBLE_ABBREVIATION?: number;
  };
}

interface SingleDomainResult {
  success: boolean;
  domain?: string;
  create_date?: string;
  domain_age_days?: number;
  registrar?: string;
  registrant_country?: string;
  risk_flags?: string[];
  error?: string;
  is_primary?: boolean;
}

interface DomainResults {
  success: boolean;
  domain?: string;
  create_date?: string;
  domain_age_days?: number;
  registrar?: string;
  registrant_country?: string;
  risk_flags?: string[];
  error?: string;
  // Multi-domain fields
  domains?: SingleDomainResult[];
  primary_domain?: string;
  total_checked?: number;
  queries?: Array<{ domain: string; endpoint: string; is_primary: boolean }>;
}

interface TokenUsage {
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  estimated_cost_usd?: number;
}

interface ExecutiveBrief {
  narrative: string;
  cited_sources: Array<{ label: string; url: string }>;
  prompt_sent?: string;
}

interface ComprehensiveResults {
  company_name: string;
  enrichment?: EnrichmentResult;
  csl_results: SearchResponse;
  match_validation?: MatchValidationResult;
  domain_results?: DomainResults;
  ai_research?: {
    success: boolean;
    research?: AIResearch;
    token_usage?: TokenUsage;
    error?: string;
    prompt_sent?: string;
  };
  opensanctions?: OpenSanctionsResponse;
  risk_assessment?: {
    risk_level: string;
    risk_factors: Array<string | { text: string; severity: string }>;
    recommendation: string;
    source_risks?: {
      [key: string]: {
        level: string;
        summary: string;
      };
    };
    report_id?: string;
    screening_timestamp?: string;
  };
  executive_brief?: ExecutiveBrief | null;
  token_usage?: TokenUsage;
  screened_entities?: Array<{ legal_name: string; country?: string }>;
  excluded_companies?: Array<{ legal_name: string; country?: string }>;
  corporate_structure?: {
    root_entity: string;
    entities: CorporateEntity[];
    relationships: Array<{ parent: string; child: string; type: string }>;
    sources?: Array<{ title: string; url: string }>;
    notes?: string;
  };
  csl_recheck?: any;
}

interface CompanyCandidate {
  legal_name: string;
  country?: string | null;
  city?: string | null;
  full_address?: string | null;
  website?: string | null;
  industry?: string | null;
  description?: string | null;
  confidence?: string;
}

interface PreviewResponse {
  requires_disambiguation: boolean;
  candidates: CompanyCandidate[];
  reason?: string;
  error?: string;
  prompt_sent?: string;
  sources?: Array<{ title: string; url: string }>;
}

interface CorporateEntity {
  legal_name: string;
  relationship: string;
  country?: string | null;
  city?: string | null;
  industry?: string | null;
  description?: string | null;
  ownership_pct?: string | null;
  confidence?: string;
  csl_status?: 'MATCHED' | 'CLEAR';
  discovered_via?: string;
}

interface CorporateStructureResult {
  root_entity: string;
  entities: CorporateEntity[];
  relationships: Array<{ parent: string; child: string; type: string }>;
  sources?: Array<{ title: string; url: string }>;
  notes?: string;
  error?: string;
  prompt_sent?: string;
}

interface TransparencyStep {
  step: number;
  label: string;
  type: 'api' | 'ai' | 'user';
  endpoint?: string;
  model?: string;
  prompt?: string;
  requestSummary?: string;
  responseSummary?: string;
}

interface HistoryItem {
  id: number;
  task_id: string;
  company_name: string;
  domain?: string;
  country?: string;
  risk_level?: string;
  include_opensanctions: boolean;
  created_at: string;
  results?: ComprehensiveResults;
  total_input_tokens?: number;
  total_output_tokens?: number;
  total_tokens?: number;
  estimated_cost_usd?: number;
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function safeExternalUrl(url: string): string | null {
  try {
    const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
}

// Complete list of countries with ISO 3166-1 alpha-2 codes
const COUNTRIES = [
  { code: '', name: 'Select a country (optional)' },
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AX', name: 'Åland Islands' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'AS', name: 'American Samoa' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AO', name: 'Angola' },
  { code: 'AI', name: 'Anguilla' },
  { code: 'AQ', name: 'Antarctica' },
  { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AW', name: 'Aruba' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BM', name: 'Bermuda' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BQ', name: 'Bonaire, Sint Eustatius and Saba' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BV', name: 'Bouvet Island' },
  { code: 'BR', name: 'Brazil' },
  { code: 'IO', name: 'British Indian Ocean Territory' },
  { code: 'BN', name: 'Brunei Darussalam' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BI', name: 'Burundi' },
  { code: 'CV', name: 'Cabo Verde' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CA', name: 'Canada' },
  { code: 'KY', name: 'Cayman Islands' },
  { code: 'CF', name: 'Central African Republic' },
  { code: 'TD', name: 'Chad' },
  { code: 'CL', name: 'Chile' },
  { code: 'CN', name: 'China' },
  { code: 'CX', name: 'Christmas Island' },
  { code: 'CC', name: 'Cocos (Keeling) Islands' },
  { code: 'CO', name: 'Colombia' },
  { code: 'KM', name: 'Comoros' },
  { code: 'CG', name: 'Congo' },
  { code: 'CD', name: 'Congo (Democratic Republic)' },
  { code: 'CK', name: 'Cook Islands' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CI', name: "Côte d'Ivoire" },
  { code: 'HR', name: 'Croatia' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CW', name: 'Curaçao' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czechia' },
  { code: 'DK', name: 'Denmark' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DM', name: 'Dominica' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EG', name: 'Egypt' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'GQ', name: 'Equatorial Guinea' },
  { code: 'ER', name: 'Eritrea' },
  { code: 'EE', name: 'Estonia' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FK', name: 'Falkland Islands' },
  { code: 'FO', name: 'Faroe Islands' },
  { code: 'FJ', name: 'Fiji' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'GF', name: 'French Guiana' },
  { code: 'PF', name: 'French Polynesia' },
  { code: 'TF', name: 'French Southern Territories' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GE', name: 'Georgia' },
  { code: 'DE', name: 'Germany' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GI', name: 'Gibraltar' },
  { code: 'GR', name: 'Greece' },
  { code: 'GL', name: 'Greenland' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GP', name: 'Guadeloupe' },
  { code: 'GU', name: 'Guam' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GG', name: 'Guernsey' },
  { code: 'GN', name: 'Guinea' },
  { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HT', name: 'Haiti' },
  { code: 'HM', name: 'Heard Island and McDonald Islands' },
  { code: 'VA', name: 'Holy See' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IR', name: 'Iran' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IM', name: 'Isle of Man' },
  { code: 'IL', name: 'Israel' },
  { code: 'IT', name: 'Italy' },
  { code: 'JM', name: 'Jamaica' },
  { code: 'JP', name: 'Japan' },
  { code: 'JE', name: 'Jersey' },
  { code: 'JO', name: 'Jordan' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'KP', name: 'Korea (North)' },
  { code: 'KR', name: 'Korea (South)' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'LA', name: 'Laos' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LY', name: 'Libya' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MO', name: 'Macao' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MV', name: 'Maldives' },
  { code: 'ML', name: 'Mali' },
  { code: 'MT', name: 'Malta' },
  { code: 'MH', name: 'Marshall Islands' },
  { code: 'MQ', name: 'Martinique' },
  { code: 'MR', name: 'Mauritania' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'YT', name: 'Mayotte' },
  { code: 'MX', name: 'Mexico' },
  { code: 'FM', name: 'Micronesia' },
  { code: 'MD', name: 'Moldova' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MS', name: 'Montserrat' },
  { code: 'MA', name: 'Morocco' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'NA', name: 'Namibia' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NC', name: 'New Caledonia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'NU', name: 'Niue' },
  { code: 'NF', name: 'Norfolk Island' },
  { code: 'MK', name: 'North Macedonia' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'NO', name: 'Norway' },
  { code: 'OM', name: 'Oman' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PW', name: 'Palau' },
  { code: 'PS', name: 'Palestine' },
  { code: 'PA', name: 'Panama' },
  { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'PE', name: 'Peru' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PN', name: 'Pitcairn' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RE', name: 'Réunion' },
  { code: 'RO', name: 'Romania' },
  { code: 'RU', name: 'Russia' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'BL', name: 'Saint Barthélemy' },
  { code: 'SH', name: 'Saint Helena' },
  { code: 'KN', name: 'Saint Kitts and Nevis' },
  { code: 'LC', name: 'Saint Lucia' },
  { code: 'MF', name: 'Saint Martin' },
  { code: 'PM', name: 'Saint Pierre and Miquelon' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines' },
  { code: 'WS', name: 'Samoa' },
  { code: 'SM', name: 'San Marino' },
  { code: 'ST', name: 'Sao Tome and Principe' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SN', name: 'Senegal' },
  { code: 'RS', name: 'Serbia' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SX', name: 'Sint Maarten' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SB', name: 'Solomon Islands' },
  { code: 'SO', name: 'Somalia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'GS', name: 'South Georgia and the South Sandwich Islands' },
  { code: 'SS', name: 'South Sudan' },
  { code: 'ES', name: 'Spain' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SJ', name: 'Svalbard and Jan Mayen' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'SY', name: 'Syria' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TJ', name: 'Tajikistan' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TL', name: 'Timor-Leste' },
  { code: 'TG', name: 'Togo' },
  { code: 'TK', name: 'Tokelau' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TT', name: 'Trinidad and Tobago' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TR', name: 'Turkey' },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'TC', name: 'Turks and Caicos Islands' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'UG', name: 'Uganda' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'UM', name: 'United States Minor Outlying Islands' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'VG', name: 'Virgin Islands (British)' },
  { code: 'VI', name: 'Virgin Islands (U.S.)' },
  { code: 'WF', name: 'Wallis and Futuna' },
  { code: 'EH', name: 'Western Sahara' },
  { code: 'YE', name: 'Yemen' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' },
];

// Progress steps for comprehensive check
const PROGRESS_STEPS = [
  { id: 1, name: 'Enriching company profile' },
  { id: 2, name: 'Searching CSL database' },
  { id: 3, name: 'Validating matches' },
  { id: 4, name: 'Rechecking coincidental matches' },
  { id: 5, name: 'Running AI research' },
  { id: 6, name: 'Checking domain intelligence' },
  { id: 7, name: 'Generating risk assessment' },
  { id: 8, name: 'Generating executive brief' },
];

// CSL filter tabs
type CSLFilterTab = 'all' | 'confirmed' | 'uncertain' | 'coincidental' | 'filtered';

// Screening sub-tabs
type ScreeningSubTab = 'corporate-structure' | 'csl-matches' | 'additional-screening';

interface SearchPageProps {
  onResultsChange?: (results: ComprehensiveResults | null) => void;
  onCompanyNameChange?: (name: string) => void;
  loadScreeningId?: number | null;
  onScreeningLoaded?: () => void;
  resetTrigger?: number;
}

const SearchPage: React.FC<SearchPageProps> = ({
  onResultsChange,
  onCompanyNameChange,
  loadScreeningId,
  onScreeningLoaded,
  resetTrigger,
}) => {
  const { isAdmin } = useAuth();
  const [companyName, setCompanyName] = useState('');
  const [country, setCountry] = useState('');
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [comprehensiveResults, setComprehensiveResults] = useState<ComprehensiveResults | null>(null);
  const [error, setError] = useState<string | null>(null);

  // On-demand OpenSanctions state
  const [opensanctionsLoading, setOpensanctionsLoading] = useState(false);
  const [opensanctionsError, setOpensanctionsError] = useState<string | null>(null);

  // Domain retry state
  const [domainRetryLoading, setDomainRetryLoading] = useState<Set<string>>(new Set());

  // Disambiguation state
  const [showDisambiguation, setShowDisambiguation] = useState(false);
  const [disambiguationCandidates, setDisambiguationCandidates] = useState<CompanyCandidate[]>([]);
  const [disambiguationReason, setDisambiguationReason] = useState<string>('');
  const [disambiguationSources, setDisambiguationSources] = useState<Array<{ title: string; url: string }>>([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Corporate structure state
  const [showCorporateStructure, setShowCorporateStructure] = useState(false);
  const [corporateStructure, setCorporateStructure] = useState<CorporateStructureResult | null>(null);
  const [structureLoading, setStructureLoading] = useState(false);
  const [pendingCompanies, setPendingCompanies] = useState<CompanyCandidate[]>([]);

  // UI state
  const [formVisible, setFormVisible] = useState(true);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [cslFilterTab, setCslFilterTab] = useState<CSLFilterTab>('all');
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());

  // Transparency log state
  const [transparencyLog, setTransparencyLog] = useState<TransparencyStep[]>([]);

  // CSL accordion state
  const [expandedCSLRows, setExpandedCSLRows] = useState<Set<number>>(new Set());

  // Normalize risk level strings for CSS class names (e.g. "NO FINDINGS" → "no-findings")
  const riskClass = (level: string) => level.toLowerCase().replace(/\s+/g, '-');

  // Screening sub-tab state
  const [screeningSubTab, setScreeningSubTab] = useState<ScreeningSubTab>('csl-matches');

  // Collapsible sections state (smart defaults set via useEffect when results arrive)
  const [sectionsExpanded, setSectionsExpanded] = useState({
    executiveBrief: true,
    screeningResults: true,
    corporateStructure: false,
    companyIntelligence: false,
    domain: false,
    methodology: false,
  });

  // Track which screening the sections were last set for.
  // Only reset sections when a genuinely new screening arrives, not on in-place patches.
  const lastSectionsScreeningRef = useRef<string | null>(null);

  useEffect(() => {
    if (!comprehensiveResults) return;
    const screeningKey = comprehensiveResults.company_name || '';
    if (lastSectionsScreeningRef.current === screeningKey) return;
    lastSectionsScreeningRef.current = screeningKey;

    const hasCSLMatches = (comprehensiveResults.csl_results?.total || 0) > 0;
    const hasConfirmedCSL = (comprehensiveResults.match_validation?.summary?.confirmed_count || 0) > 0;
    const hasOpenSanctions = (comprehensiveResults.opensanctions?.sanctioned_count || 0) > 0 ||
                             (comprehensiveResults.opensanctions?.high_risk_count || 0) > 0;
    const hasCorpStructure = (comprehensiveResults.corporate_structure?.entities?.length || 0) > 1;
    setSectionsExpanded({
      executiveBrief: true,
      screeningResults: hasCSLMatches || hasConfirmedCSL || hasOpenSanctions,
      corporateStructure: hasCorpStructure,
      companyIntelligence: false,
      domain: false,
      methodology: false,
    });
  }, [comprehensiveResults]);

  // Notify parent of changes
  useEffect(() => {
    onResultsChange?.(comprehensiveResults);
  }, [comprehensiveResults, onResultsChange]);

  useEffect(() => {
    onCompanyNameChange?.(companyName);
  }, [companyName, onCompanyNameChange]);

  // Append transparency steps when comprehensive results arrive
  useEffect(() => {
    if (!comprehensiveResults) return;

    setTransparencyLog(prev => {
      const nextStep = prev.length > 0 ? prev[prev.length - 1].step + 1 : 1;
      let stepNum = nextStep;
      const steps: TransparencyStep[] = [];

      // CSL Search (primary)
      const csl = comprehensiveResults.csl_results;
      if (csl) {
        const searchQueries = csl.search_queries || [];
        const primaryQueries = searchQueries.filter(q => q.type === 'primary' || q.type === 'secondary' || q.type === 'original_country');
        const additionalEntityQueries = searchQueries.filter(q => q.type === 'additional_entity');
        const altLangResults = (csl.results || []).filter(r => r.searched_entity && r.search_source === 'alternate_language');

        // Primary CSL search step
        const filterCount = csl.weak_matches_filtered || 0;
        const filterSummary = csl.filter_summary || {};
        const filterReasonLabels: Record<string, string> = {
          low_score: 'low score',
          low_name_similarity: 'low name similarity',
          entity_type_mismatch: 'type mismatch',
          no_name_overlap: 'no name overlap',
        };
        const filterBreakdown = Object.entries(filterSummary)
          .map(([key, count]) => `${count} ${filterReasonLabels[key] || key}`)
          .join(', ');
        const filterText = filterCount > 0
          ? ` (${filterCount} filtered${filterBreakdown ? ': ' + filterBreakdown : ''})`
          : '';
        const primarySummary = csl.enhanced_search
          ? `Enhanced search: ${primaryQueries.length || 1} query/queries, ${csl.total || 0} match(es) found${filterText}`
          : `${csl.total || 0} match(es) found${filterText}`;

        steps.push({
          step: stepNum++,
          label: 'CSL Screening List Search',
          type: 'api',
          endpoint: csl.query_url || 'GET https://data.trade.gov/consolidated_screening_list/v1/search',
          requestSummary: csl.query_params ? JSON.stringify(csl.query_params, null, 2) : undefined,
          responseSummary: primarySummary,
        });

        // Per-entity additional CSL searches
        if (additionalEntityQueries.length > 0) {
          const entityNames = additionalEntityQueries.map(q => q.name).join(', ');
          const entityTotal = additionalEntityQueries.reduce((sum, q) => sum + (q.results_count || 0), 0);
          steps.push({
            step: stepNum++,
            label: 'Additional Entity CSL Searches',
            type: 'api',
            endpoint: 'GET https://data.trade.gov/consolidated_screening_list/v1/search',
            requestSummary: `Entities searched individually: ${entityNames}`,
            responseSummary: `${additionalEntityQueries.length} additional search(es), ${entityTotal} raw result(s) (deduplicated into main results)`,
          });
        }

        // Alternate-language name searches
        if (altLangResults.length > 0) {
          const altNames = Array.from(new Set(altLangResults.map(r => r.searched_entity || '').filter(n => n.length > 0)));
          steps.push({
            step: stepNum++,
            label: 'Alternate-Language Name CSL Searches',
            type: 'api',
            endpoint: 'GET https://data.trade.gov/consolidated_screening_list/v1/search',
            requestSummary: `Names searched: ${altNames.join(', ')}`,
            responseSummary: `${altLangResults.length} new match(es) from alternate-language names`,
          });
        }
      }

      // Match Validation
      const mv = comprehensiveResults.match_validation;
      if (mv?.prompt_sent) {
        const validationLabel = mv.partial_validation
          ? `CSL Match Validation (Top 10 of ${mv.summary?.total_matches || '?'})`
          : 'CSL Match Validation';
        const unvalidatedNote = mv.summary?.unvalidated_count
          ? `, ${mv.summary.unvalidated_count} unvalidated`
          : '';

        // Build name evidence summary for requestSummary
        const nes = mv.name_evidence_summary;
        let nameEvidenceText: string | undefined;
        if (nes && nes.signals_found > 0) {
          const parts: string[] = [];
          if (nes.SCREENED_NAME_IN_ALT_NAMES) parts.push(`${nes.SCREENED_NAME_IN_ALT_NAMES} alt-name match`);
          if (nes.SCREENED_NAME_IN_REMARKS) parts.push(`${nes.SCREENED_NAME_IN_REMARKS} remarks match`);
          if (nes.NEAR_IDENTICAL_NAME) parts.push(`${nes.NEAR_IDENTICAL_NAME} near-identical name`);
          if (nes.ACRONYM_MATCH) parts.push(`${nes.ACRONYM_MATCH} acronym match`);
          if (nes.POSSIBLE_ABBREVIATION) parts.push(`${nes.POSSIBLE_ABBREVIATION} possible abbreviation`);
          nameEvidenceText = `Name analysis: ${parts.join(', ')} (${nes.matches_with_evidence?.length || 0} match(es) with evidence)`;
        }

        steps.push({
          step: stepNum++,
          label: validationLabel,
          type: 'ai',
          model: mv.ai_provider === 'gemini' ? 'Gemini 2.0 Flash' : 'GPT-4o-mini',
          prompt: mv.prompt_sent,
          requestSummary: nameEvidenceText,
          responseSummary: mv.summary
            ? `${mv.summary.confirmed_count} confirmed, ${mv.summary.uncertain_count} uncertain, ${mv.summary.coincidental_count} coincidental${unvalidatedNote}`
            : undefined,
        });
      }

      // AI Research
      const ai = comprehensiveResults.ai_research;
      if (ai?.prompt_sent) {
        steps.push({
          step: stepNum++,
          label: 'AI Company Research',
          type: 'ai',
          model: ai.research?.ai_provider === 'gemini' ? 'Gemini 2.0 Flash' : 'GPT-4o-mini',
          prompt: ai.prompt_sent,
          responseSummary: ai.success ? `Research completed (confidence: ${ai.research?.research_confidence || 'N/A'})` : 'Research failed',
        });
      }

      // Domain Check
      const dom = comprehensiveResults.domain_results;
      if (dom) {
        const domainList = dom.queries?.map(q => q.domain).join(', ') || dom.domain || '';
        steps.push({
          step: stepNum++,
          label: 'Domain WHOIS Check',
          type: 'api',
          endpoint: 'GET https://api.ip2whois.com/v2',
          requestSummary: `Domains checked: ${domainList}`,
          responseSummary: `${dom.total_checked || 1} domain(s) checked, ${(dom.risk_flags || []).length} risk flag(s)`,
        });
      }

      // Risk Assessment
      const risk = comprehensiveResults.risk_assessment;
      if (risk) {
        steps.push({
          step: stepNum++,
          label: 'Risk Assessment Compilation',
          type: 'api',
          requestSummary: `Sources evaluated: CSL, AI Research, Domain, OpenSanctions`,
          responseSummary: `Overall risk: ${risk.risk_level}, ${risk.risk_factors?.length || 0} factor(s) identified`,
        });
      }

      // Executive Brief
      const brief = comprehensiveResults.executive_brief;
      if (brief?.prompt_sent) {
        steps.push({
          step: stepNum++,
          label: 'Executive Brief Generation',
          type: 'ai',
          model: 'Gemini 2.0 Flash',
          prompt: brief.prompt_sent,
          responseSummary: brief.narrative ? `Brief generated (${brief.cited_sources?.length || 0} sources cited)` : 'Brief generation failed',
        });
      }

      return [...prev, ...steps];
    });
  }, [comprehensiveResults]);

  // Load screening from history
  useEffect(() => {
    if (loadScreeningId) {
      loadHistoryItem(loadScreeningId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadScreeningId]);

  // Scroll listener for back-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Reset state when resetTrigger changes (New Screening clicked)
  useEffect(() => {
    if (resetTrigger && resetTrigger > 0) {
      setCompanyName('');
      setCountry('');
      setDomain('');
      setResults(null);
      setComprehensiveResults(null);
      lastSectionsScreeningRef.current = null;
      setError(null);
      setFormVisible(true);
      setCurrentStep(0);
      setCslFilterTab('all');
      setOpensanctionsLoading(false);
      setOpensanctionsError(null);
      setShowDisambiguation(false);
      setDisambiguationCandidates([]);
      setDisambiguationReason('');
      setDisambiguationSources([]);
      setPreviewLoading(false);
      setShowCorporateStructure(false);
      setCorporateStructure(null);
      setStructureLoading(false);
      setPendingCompanies([]);
      setTransparencyLog([]);
      setExpandedSteps(new Set());
      setScreeningSubTab('csl-matches');
    }
  }, [resetTrigger]);

  // Auto-expand confirmed CSL matches; reset on tab change
  useEffect(() => {
    if (!comprehensiveResults?.match_validation?.validations) {
      setExpandedCSLRows(new Set());
      return;
    }
    const validations = comprehensiveResults.match_validation.validations;
    const autoExpand = new Set<number>();
    filteredCSLResults.forEach((result, index) => {
      const originalIndex = displayResults?.results?.findIndex(r => r.name === result.name && r.source === result.source) ?? -1;
      const validation = validations.find(v => v.match_index === originalIndex + 1);
      if (validation) {
        const rel = validation.relationship;
        if (rel === 'EXACT' || rel === 'SUBSIDIARY' || rel === 'AFFILIATED') {
          autoExpand.add(index);
        }
      }
    });
    // If only 1 result, auto-expand it regardless
    if (filteredCSLResults.length === 1) {
      autoExpand.add(0);
    }
    setExpandedCSLRows(autoExpand);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cslFilterTab, comprehensiveResults?.match_validation]);

  const loadHistoryItem = async (id: number) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get<HistoryItem>(`${API_BASE}/history/${id}`);
      if (response.data.results) {
        setComprehensiveResults(response.data.results);
        setCompanyName(response.data.company_name);
        if (response.data.domain) setDomain(response.data.domain);
        if (response.data.country) setCountry(response.data.country);
        setResults(null);
        setFormVisible(false);
      }
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError('You are making requests too quickly. Please wait a moment and try again.');
      } else {
        setError(err.response?.data?.error || 'Failed to load screening');
      }
    } finally {
      setLoading(false);
      onScreeningLoaded?.();
    }
  };

  const handleComprehensiveCheck = async () => {
    setError(null);
    setResults(null);
    setComprehensiveResults(null);
    lastSectionsScreeningRef.current = null;
    setShowDisambiguation(false);
    setCslFilterTab('all');
    setTransparencyLog([]);
    setExpandedSteps(new Set());

    // Step 1: Run preview to check for disambiguation
    setPreviewLoading(true);
    setFormVisible(false);

    try {
      const previewResponse = await axios.post<PreviewResponse>(`${API_BASE}/preview`, {
        name: companyName,
        country: country || undefined,
        domain: domain || undefined,
      });

      const preview = previewResponse.data;

      // Append transparency step for preview
      const newSteps: TransparencyStep[] = [];
      newSteps.push({
        step: 1,
        label: 'Company Preview Search',
        type: 'ai',
        model: 'Gemini 2.0 Flash + Google Search',
        endpoint: 'Gemini generateContent API (with Google Search grounding)',
        requestSummary: `Search params: name="${companyName}"${country ? `, country="${country}"` : ''}${domain ? `, domain="${domain}"` : ''}`,
        responseSummary: `${preview.candidates.length} candidate(s) found, requires_disambiguation: ${preview.requires_disambiguation}`,
        prompt: preview.prompt_sent,
      });
      setTransparencyLog(newSteps);

      if (preview.requires_disambiguation && preview.candidates.length > 0) {
        // Show disambiguation panel
        setDisambiguationCandidates(preview.candidates);
        setDisambiguationReason(preview.reason || '');
        setDisambiguationSources(preview.sources || []);
        setShowDisambiguation(true);
        setPreviewLoading(false);
        return;
      }

      // No disambiguation needed - proceed to structure discovery
      setPreviewLoading(false);
      if (preview.candidates.length === 1) {
        discoverCorporateStructure([preview.candidates[0]]);
      } else {
        // No candidates — build from form input
        discoverCorporateStructure([{ legal_name: companyName, country: country || undefined }]);
      }

    } catch (err: any) {
      if (err.response?.status === 429) {
        setPreviewLoading(false);
        setFormVisible(true);
        setError('You are making requests too quickly. Please wait a moment and try again.');
        return;
      }
      // Preview failed - build minimal candidate, try structure discovery
      setPreviewLoading(false);
      discoverCorporateStructure([{ legal_name: companyName, country: country || undefined }]);
    }
  };

  const startComprehensiveCheck = async (
    selectedCompanies: CompanyCandidate[] | null,
    excludedCompanies?: CompanyCandidate[]
  ) => {
    setLoading(true);
    setError(null);
    setCurrentStep(1);
    setShowDisambiguation(false);

    try {
      const response = await axios.post(`${API_BASE}/check/comprehensive`, {
        name: companyName,
        country: country || undefined,
        domain: domain || undefined,
        selected_companies: selectedCompanies?.length ? selectedCompanies : undefined,
        excluded_companies: excludedCompanies?.length ? excludedCompanies : undefined,
        corporate_structure: corporateStructure ? {
          root_entity: corporateStructure.root_entity,
          entities: corporateStructure.entities,
          relationships: corporateStructure.relationships,
          sources: corporateStructure.sources,
          notes: corporateStructure.notes,
        } : undefined,
      });

      const taskId = response.data.task_id;
      pollTaskStatus(taskId);
    } catch (err: any) {
      if (err.response?.status === 429) {
        setError('You are making requests too quickly. Please wait a moment and try again.');
      } else {
        setError(err.response?.data?.error || 'An error occurred');
      }
      setLoading(false);
      setFormVisible(true);
    }
  };

  const handleDisambiguationConfirm = (selected: CompanyCandidate[], excluded: CompanyCandidate[]) => {
    setTransparencyLog(prev => {
      const nextStep = prev.length > 0 ? prev[prev.length - 1].step + 1 : 1;
      return [...prev, {
        step: nextStep,
        label: 'User Company Selection',
        type: 'user' as const,
        requestSummary: `Selected: ${selected.map(c => c.legal_name).join(', ')}` +
          (excluded.length > 0 ? `\nExcluded: ${excluded.map(c => c.legal_name).join(', ')}` : ''),
        responseSummary: `${selected.length} company/companies selected for screening`,
      }];
    });
    discoverCorporateStructure(selected);
  };

  const discoverCorporateStructure = async (companies: CompanyCandidate[]) => {
    setPendingCompanies(companies);
    setShowDisambiguation(false);
    setStructureLoading(true);
    setFormVisible(false);

    try {
      const response = await axios.post(`${API_BASE}/corporate-structure`, {
        selected_companies: companies,
      });
      const structData = response.data as CorporateStructureResult;
      setCorporateStructure(structData);
      setShowCorporateStructure(true);

      // Append transparency step
      setTransparencyLog(prev => {
        const nextStep = prev.length > 0 ? prev[prev.length - 1].step + 1 : 1;
        return [...prev, {
          step: nextStep,
          label: 'Corporate Structure Discovery',
          type: 'ai' as const,
          model: 'Gemini 2.0 Flash + Google Search',
          endpoint: 'Gemini generateContent API (with Google Search grounding)',
          requestSummary: `Companies: ${companies.map(c => c.legal_name).join(', ')}`,
          responseSummary: `${structData.entities?.length || 0} entities discovered, root: ${structData.root_entity}`,
          prompt: structData.prompt_sent,
        }];
      });
    } catch {
      // Structure discovery failed — proceed without it
      startComprehensiveCheck(companies);
    } finally {
      setStructureLoading(false);
    }
  };

  const handleStructureConfirm = (selected: CompanyCandidate[], excluded: CompanyCandidate[]) => {
    setShowCorporateStructure(false);
    startComprehensiveCheck(selected, excluded);
  };

  const handleStructureSkip = () => {
    setShowCorporateStructure(false);
    startComprehensiveCheck(pendingCompanies);
  };

  const handleDisambiguationBack = () => {
    setShowDisambiguation(false);
    setDisambiguationCandidates([]);
    setDisambiguationReason('');
    setDisambiguationSources([]);
    setFormVisible(true);
  };

  const pollTaskStatus = async (taskId: string) => {
    const maxAttempts = 150; // 150 * 2s = 5 min timeout for complex entities
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await axios.get(`${API_BASE}/check/status/${taskId}`);

        const statusInfo = response.data.status;
        let statusText = '';
        if (typeof statusInfo === 'string') {
          statusText = statusInfo.toLowerCase();
        } else if (statusInfo && typeof statusInfo === 'object') {
          const stepOrStatus = statusInfo.step || statusInfo.status || '';
          statusText = String(stepOrStatus).toLowerCase();
        }

        if (statusText) {
          if (statusText.includes('enrich')) setCurrentStep(1);
          else if (statusText.includes('csl') || statusText.includes('screening')) setCurrentStep(2);
          else if (statusText.includes('recheck')) setCurrentStep(4);
          else if (statusText.includes('validat')) setCurrentStep(3);
          else if (statusText.includes('ai') || statusText.includes('research')) setCurrentStep(5);
          else if (statusText.includes('domain')) setCurrentStep(6);
          else if (statusText.includes('risk') || statusText.includes('assess')) setCurrentStep(7);
          else if (statusText.includes('executive') || statusText.includes('brief')) setCurrentStep(8);
        }

        if (response.data.state === 'SUCCESS') {
          const result = response.data.result;
          // Prefer Celery-returned corporate_structure (may be enhanced with CSL annotations)
          // Fall back to frontend-cached version
          if (!result.corporate_structure && corporateStructure) {
            result.corporate_structure = {
              root_entity: corporateStructure.root_entity,
              entities: corporateStructure.entities,
              relationships: corporateStructure.relationships,
              sources: corporateStructure.sources,
              notes: corporateStructure.notes,
            };
          }
          setComprehensiveResults(result);
          setLoading(false);
          setFormVisible(false);
          setCurrentStep(0);
        } else if (response.data.state === 'FAILURE') {
          setError(response.data.error || 'Task failed');
          setLoading(false);
          setCurrentStep(0);
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setError('Task timed out');
          setLoading(false);
          setCurrentStep(0);
        }
      } catch (err: any) {
        // Log without potentially sensitive task data
        console.error('Poll error');
        if (attempts >= 3 || (err.response && err.response.status >= 400)) {
          setError('Error checking task status: ' + (err.message || 'Unknown error'));
          setLoading(false);
          setCurrentStep(0);
        } else {
          attempts++;
          setTimeout(poll, 2000);
        }
      }
    };

    poll();
  };

  const toggleSection = (section: keyof typeof sectionsExpanded) => {
    setSectionsExpanded(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleCSLRow = (index: number) => {
    setExpandedCSLRows(prev => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const displayResults = comprehensiveResults?.csl_results || results;

  // Get display name for the company
  const displayCompanyName = comprehensiveResults?.enrichment?.profile?.legal_name
    || comprehensiveResults?.company_name
    || companyName;

  // Filter CSL results based on active tab
  const getFilteredCSLResults = (): SearchResult[] => {
    if (!displayResults?.results) return [];

    const validations = comprehensiveResults?.match_validation?.validations || [];

    if (cslFilterTab === 'all') {
      return displayResults.results;
    }

    if (cslFilterTab === 'filtered') {
      return displayResults.filtered_results || [];
    }

    return displayResults.results.filter((_, index) => {
      const validation = validations.find(v => v.match_index === index + 1);
      if (!validation) return false; // No validation = don't include in filtered views

      const relationship = validation.relationship;
      if (cslFilterTab === 'confirmed') {
        return relationship === 'EXACT' || relationship === 'SUBSIDIARY' || relationship === 'AFFILIATED';
      }
      if (cslFilterTab === 'uncertain') {
        return relationship === 'UNKNOWN';
      }
      if (cslFilterTab === 'coincidental') {
        return relationship === 'COINCIDENTAL';
      }
      return true;
    });
  };

  // Get counts for tabs
  const getTabCounts = () => {
    const summary = comprehensiveResults?.match_validation?.summary;
    const filteredCount = (displayResults as any)?.weak_matches_filtered || 0;

    if (summary) {
      return {
        all: displayResults?.results?.length || 0,
        confirmed: summary.confirmed_count,
        uncertain: summary.uncertain_count,
        coincidental: summary.coincidental_count,
        filtered: filteredCount,
      };
    }

    return {
      all: displayResults?.results?.length || 0,
      confirmed: 0,
      uncertain: 0,
      coincidental: 0,
      filtered: filteredCount,
    };
  };

  const tabCounts = getTabCounts();
  const filteredCSLResults = getFilteredCSLResults();

  // Badge computations for section headers
  const screeningBadgeText = (() => {
    const parts: string[] = [];
    const confirmed = comprehensiveResults?.match_validation?.summary?.confirmed_count || 0;
    const cslTotal = displayResults?.total || 0;
    if (confirmed > 0) {
      parts.push(`${confirmed} confirmed`);
    } else if (cslTotal > 0) {
      parts.push(`${cslTotal} CSL match${cslTotal !== 1 ? 'es' : ''}`);
    }
    const osResults = comprehensiveResults?.opensanctions;
    if (osResults?.selected && osResults.success) {
      const sanctioned = osResults.sanctioned_count || 0;
      if (sanctioned > 0) parts.push(`${sanctioned} sanctioned (intl)`);
    }
    return parts.length > 0 ? parts.join(', ') : 'No matches';
  })();

  const opensanctionsBadgeText = (() => {
    const os = comprehensiveResults?.opensanctions;
    if (!os || !os.selected) return '(not run)';
    if (!os.success) return '(error)';
    const sanctioned = os.sanctioned_count || 0;
    if (sanctioned > 0) return `(${sanctioned} sanctioned)`;
    return `(${os.total || 0} matches)`;
  })();

  const companyIntelBadgeText = (() => {
    const conf = comprehensiveResults?.ai_research?.research?.research_confidence;
    if (conf) return conf === 'HIGH' ? 'High confidence' : conf === 'LOW' ? 'Low confidence' : `${conf} confidence`;
    return '';
  })();

  const domainBadgeText = (() => {
    const dr = comprehensiveResults?.domain_results;
    if (!dr) return '';
    const flags = dr.risk_flags?.length || 0;
    const checked = dr.total_checked || 1;
    if (flags > 0) return `${flags} flag${flags !== 1 ? 's' : ''}`;
    return `${checked} domain${checked !== 1 ? 's' : ''} checked`;
  })();

  // Run on-demand OpenSanctions check
  const runOpenSanctionsCheck = async () => {
    if (!comprehensiveResults) return;
    setOpensanctionsLoading(true);
    setOpensanctionsError(null);

    // Use legal name if available, otherwise fall back to company name (single query = $0.10)
    const bestName = comprehensiveResults.enrichment?.profile?.legal_name
      || comprehensiveResults.company_name
      || '';

    if (!bestName) return;

    try {
      const response = await axios.post<OpenSanctionsResponse>(`${API_BASE}/check/opensanctions`, {
        names: [bestName],
        country: country || undefined,
      });

      const updatedResults = { ...comprehensiveResults, opensanctions: response.data };
      setComprehensiveResults(prev => prev ? {
        ...prev,
        opensanctions: response.data,
      } : prev);

      // Regenerate executive brief to incorporate OpenSanctions data
      try {
        const regenResponse = await axios.post(`${API_BASE}/check/regenerate-brief`, {
          results: updatedResults,
        });
        if (regenResponse.data?.risk_assessment) {
          setComprehensiveResults(prev => prev ? {
            ...prev,
            risk_assessment: regenResponse.data.risk_assessment,
            ...(regenResponse.data.executive_brief ? { executive_brief: regenResponse.data.executive_brief } : {}),
          } : prev);
        }
      } catch (regenErr) {
        console.warn('Failed to regenerate executive brief after OpenSanctions:', regenErr);
      }
    } catch (err: any) {
      if (err.response?.status === 429) {
        setOpensanctionsError('Too many requests. Please wait a moment and try again.');
      } else {
        setOpensanctionsError(err.response?.data?.error || 'OpenSanctions screening failed. Please try again.');
      }
    } finally {
      setOpensanctionsLoading(false);
    }
  };

  // Retry a failed domain WHOIS lookup
  const retryDomainCheck = async (domainToRetry: string) => {
    if (!comprehensiveResults || !domainToRetry) return;
    setDomainRetryLoading(prev => new Set(prev).add(domainToRetry));

    try {
      const response = await axios.post(`${API_BASE}/domain/check`, { domain: domainToRetry });
      const newResult = response.data;

      setComprehensiveResults(prev => {
        if (!prev?.domain_results) return prev;
        const updated = { ...prev, domain_results: { ...prev.domain_results } };

        // Update in multi-domain list
        if (updated.domain_results.domains) {
          updated.domain_results.domains = updated.domain_results.domains.map(
            (dr: SingleDomainResult) => dr.domain === domainToRetry
              ? { ...newResult, is_primary: dr.is_primary }
              : dr
          );
        }

        // Update top-level if this is the primary domain
        if (updated.domain_results.domain === domainToRetry || updated.domain_results.primary_domain === domainToRetry) {
          updated.domain_results = {
            ...updated.domain_results,
            success: newResult.success,
            create_date: newResult.create_date,
            domain_age_days: newResult.domain_age_days,
            registrar: newResult.registrar,
            registrant_country: newResult.registrant_country,
            risk_flags: newResult.risk_flags,
            error: newResult.error,
          };
        }

        return updated;
      });
    } catch {
      // Leave original error in place if retry also fails
    } finally {
      setDomainRetryLoading(prev => {
        const next = new Set(prev);
        next.delete(domainToRetry);
        return next;
      });
    }
  };

  return (
    <div className="search-page">
      {/* Search Form */}
      {formVisible && (
        <form onSubmit={(e) => { e.preventDefault(); handleComprehensiveCheck(); }} className="search-form">
          <div className="form-group">
            <label htmlFor="companyName">Company Name *</label>
            <input
              type="text"
              id="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Enter company name"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="country">Country (optional)</label>
              <select
                id="country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="country-select"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="domain">Website Domain (optional)</label>
              <input
                type="text"
                id="domain"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="e.g., example.com"
              />
            </div>
          </div>

          <div className="button-group">
            <button
              type="submit"
              disabled={loading || previewLoading || !companyName}
              className="primary"
            >
              {loading ? 'Screening...' : previewLoading ? 'Checking...' : 'Run Screening'}
            </button>
          </div>
        </form>
      )}

      {/* Preview Loading Indicator */}
      {previewLoading && (
        <div className="simple-loading">
          <div className="progress-spinner"></div>
          <span>Finding matching companies...</span>
        </div>
      )}

      {/* Disambiguation Panel */}
      {showDisambiguation && (
        <DisambiguationPanel
          candidates={disambiguationCandidates}
          reason={disambiguationReason}
          companyName={companyName}
          sources={disambiguationSources}
          onConfirm={handleDisambiguationConfirm}
          onBack={handleDisambiguationBack}
        />
      )}

      {/* Corporate Structure Loading */}
      {structureLoading && (
        <div className="simple-loading">
          <div className="progress-spinner"></div>
          <span>Discovering corporate structure...</span>
        </div>
      )}

      {/* Corporate Structure Panel */}
      {showCorporateStructure && corporateStructure && (
        <CorporateStructurePanel
          structure={corporateStructure}
          originalSelections={pendingCompanies}
          onConfirm={handleStructureConfirm}
          onSkip={handleStructureSkip}
        />
      )}

      {/* Progress Indicator */}
      {loading && currentStep > 0 && (
        <div className="progress-indicator">
          <div className="progress-header">
            <div className="progress-spinner"></div>
            <span>Running comprehensive check...</span>
          </div>
          <div className="progress-steps">
            {PROGRESS_STEPS.map((step) => (
              <div
                key={step.id}
                className={`progress-step ${
                  step.id < currentStep ? 'completed' :
                  step.id === currentStep ? 'active' : ''
                }`}
              >
                <span className="step-indicator">
                  {step.id < currentStep ? '✓' : step.id}
                </span>
                <span className="step-name">{step.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Simple Loading Indicator (for quick search) */}
      {loading && currentStep === 0 && (
        <div className="simple-loading">
          <div className="progress-spinner"></div>
          <span>Searching...</span>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {/* Company Name Header - Simple header showing what company we're looking at */}
      {comprehensiveResults && (
        <div className="company-header-banner">
          <div className="company-header-left">
            <h2>{displayCompanyName}</h2>
            {comprehensiveResults.enrichment?.profile?.country && (
              <span className="company-country">{comprehensiveResults.enrichment.profile.country}</span>
            )}
            {domain && safeExternalUrl(domain) && (
              <a href={safeExternalUrl(domain)!} target="_blank" rel="noopener noreferrer" className="company-domain">
                {domain}
              </a>
            )}
            {comprehensiveResults.enrichment?.profile?.trade_names && comprehensiveResults.enrichment.profile.trade_names.length > 0 && (
              <span className="also-screened">
                Also screened: {comprehensiveResults.enrichment.profile.trade_names.join(', ')}
              </span>
            )}
          </div>
          {comprehensiveResults.risk_assessment?.report_id && (
            <div className="company-header-meta">
              <span className="report-id">{comprehensiveResults.risk_assessment.report_id}</span>
              {comprehensiveResults.risk_assessment.screening_timestamp && (
                <span className="screening-timestamp">
                  {new Date(comprehensiveResults.risk_assessment.screening_timestamp).toLocaleString()}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Screening Timestamp Disclaimer */}
      {comprehensiveResults?.risk_assessment?.screening_timestamp && (
        <div className="screening-disclaimer">
          This screening reflects data as of {new Date(comprehensiveResults.risk_assessment.screening_timestamp).toLocaleString()}.
          Consolidated screening lists are updated frequently via Federal Register notices. Re-screen before each transaction.
        </div>
      )}

      {/* 1. Executive Brief */}
      {comprehensiveResults?.risk_assessment && (
        <div id="executive-brief-section" className="results-section executive-brief-section">
          <div
            className="section-header collapsible"
            onClick={() => toggleSection('executiveBrief')}
          >
            <h2>
              Executive Brief
              <span className={`section-badge risk-badge-${riskClass(comprehensiveResults.risk_assessment.risk_level)}`}>
                {comprehensiveResults.risk_assessment.risk_level}
              </span>
            </h2>
            <span className="toggle-icon">{sectionsExpanded.executiveBrief ? '−' : '+'}</span>
          </div>

          {sectionsExpanded.executiveBrief && (
            <div className="section-content">
              <div className={`risk-level-display risk-${riskClass(comprehensiveResults.risk_assessment.risk_level)}`}>
                <span className="risk-level-label">Screening Summary</span>
                <span className={`risk-level-badge risk-badge-${riskClass(comprehensiveResults.risk_assessment.risk_level)}`}>
                  {comprehensiveResults.risk_assessment.risk_level}
                </span>
              </div>

              {comprehensiveResults.risk_assessment.source_risks && (
                <div className="source-risk-scorecard">
                  {[
                    { key: 'csl', label: 'CSL Screening' },
                    { key: 'ai_research', label: 'AI Research' },
                    { key: 'domain', label: 'Domain Intelligence' },
                    ...(comprehensiveResults.opensanctions?.selected
                      ? [{ key: 'opensanctions', label: 'OpenSanctions' }]
                      : []),
                  ].map(({ key, label }) => {
                    const source = comprehensiveResults.risk_assessment!.source_risks![key];
                    if (!source) return null;
                    const levelClass = riskClass(source.level);
                    const isNone = levelClass === 'none';
                    return (
                      <div key={key} className={`source-risk-row ${isNone ? 'source-not-run' : ''}`}>
                        <span className={`source-risk-flag flag-${levelClass}`}>
                          {isNone ? (
                            <svg viewBox="0 0 16 18" width="14" height="16" aria-hidden="true">
                              <rect x="1" y="0" width="2" height="18" fill="currentColor" />
                              <path d="M3 1 L14 1 L11.5 5.5 L14 10 L3 10 Z" fill="currentColor" opacity="0.4" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 16 18" width="14" height="16" aria-hidden="true">
                              <rect x="1" y="0" width="2" height="18" fill="currentColor" />
                              <path d="M3 1 L14 1 L11.5 5.5 L14 10 L3 10 Z" fill="currentColor" />
                            </svg>
                          )}
                        </span>
                        <span className="source-risk-label">{label}</span>
                        <span className="source-risk-dots" />
                        <span className={`source-risk-level level-${levelClass}`}>
                          {isNone ? 'NOT RUN' : source.level}
                        </span>
                        <span className="source-risk-summary">{source.summary}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* AI-generated narrative (or fallback) */}
              {comprehensiveResults.executive_brief?.narrative ? (
                <div className="executive-narrative">
                  {comprehensiveResults.executive_brief.narrative.split('\n\n').map((para, idx) => (
                    <p key={idx}>{para}</p>
                  ))}
                </div>
              ) : (
                <>
                  {/* Fallback: company overview + key findings bullets + recommendation */}
                  {comprehensiveResults.ai_research?.research?.company_overview && (
                    <div className="executive-narrative fallback">
                      <p>{comprehensiveResults.ai_research.research.company_overview}</p>
                    </div>
                  )}
                  {comprehensiveResults.risk_assessment.risk_factors.length > 0 && (
                    <div className="key-findings">
                      <h4>Key Findings</h4>
                      <ul>
                        {comprehensiveResults.risk_assessment.risk_factors.map((factor, idx: number) => {
                          const text = typeof factor === 'string' ? factor : factor.text;
                          const severity = typeof factor === 'string' ? 'INFO' : (factor.severity || 'INFO');
                          return (
                            <li key={idx} className={`finding-severity-${severity.toLowerCase()}`}>{text}</li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </>
              )}

              <div className="recommendation">
                <h4>Recommendation</h4>
                <p>{comprehensiveResults.risk_assessment.recommendation}</p>
              </div>

              {/* Next Steps — End-Use/End-User Guidance */}
              <div className="next-steps">
                <h4>Next Steps</h4>
                <ul>
                  <li>Complete end-use/end-user analysis per EAR Part 744 before proceeding with any transaction.</li>
                  {comprehensiveResults.risk_assessment.risk_level === 'MATCH' && (
                    <li className="next-step-high">Consult legal counsel before any engagement. Do not proceed with exports, re-exports, or transfers without obtaining required licenses.</li>
                  )}
                  {(() => {
                    const GROUP_D1 = ['CN', 'RU', 'BY', 'MM', 'KP', 'IR', 'SY', 'CU', 'VE'];
                    const companyCountry = comprehensiveResults.enrichment?.profile?.country || country;
                    const hasMilitary = comprehensiveResults.ai_research?.research?.prc_verification?.military_connections?.has_connections;
                    if ((companyCountry && GROUP_D1.includes(companyCountry.toUpperCase())) || hasMilitary) {
                      return <li className="next-step-military">Enhanced scrutiny required for military end-use/end-user restrictions (EAR §744.21).</li>;
                    }
                    return null;
                  })()}
                  {comprehensiveResults.risk_assessment.risk_factors.some(
                    f => {
                      const text = typeof f === 'string' ? f : f.text;
                      return text.toLowerCase().includes('entity list');
                    }
                  ) && (
                    <li>Determine specific items involved and applicable ECCN to assess license requirements.</li>
                  )}
                  {comprehensiveResults.risk_assessment.risk_level === 'FLAGS' &&
                   !comprehensiveResults.opensanctions?.selected && (
                    <li>Consider running OpenSanctions screening under the <strong>Additional Screening</strong> tab
                    for broader international sanctions coverage to further assess flagged risks.</li>
                  )}
                </ul>
              </div>

              {/* Cited sources from executive brief */}
              {comprehensiveResults.executive_brief?.cited_sources &&
               comprehensiveResults.executive_brief.cited_sources.length > 0 && (
                <div className="brief-sources">
                  <h4>Sources</h4>
                  <ol className="cited-sources-list">
                    {comprehensiveResults.executive_brief.cited_sources.map((src, idx) => {
                      const srcUrl = src.url ? safeExternalUrl(src.url) : null;
                      return (
                        <li key={idx}>
                          {srcUrl ? (
                            <a href={srcUrl} target="_blank" rel="noopener noreferrer" className="reference-link">
                              {src.label}
                            </a>
                          ) : (
                            <span>{src.label}</span>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 2. Screening Results (merged CSL + OpenSanctions with sub-tabs) */}
      {(displayResults || comprehensiveResults) && (
        <div id="screening-results-section" className="results-section screening-results-section">
          <div
            className="section-header collapsible"
            onClick={() => toggleSection('screeningResults')}
          >
            <h2>
              Screening Results
              {comprehensiveResults && (
                <span className={`section-badge ${(comprehensiveResults.match_validation?.summary?.confirmed_count || 0) > 0 || (comprehensiveResults.opensanctions?.sanctioned_count || 0) > 0 ? 'badge-alert' : 'badge-clear'}`}>
                  {screeningBadgeText}
                </span>
              )}
            </h2>
            <span className="toggle-icon">{sectionsExpanded.screeningResults ? '−' : '+'}</span>
          </div>

          {sectionsExpanded.screeningResults && (
            <div className="section-content">
              {/* Screening sub-tabs */}
              <div className="screening-sub-tabs">
                <button
                  className={`screening-sub-tab ${screeningSubTab === 'corporate-structure' ? 'active' : ''}`}
                  onClick={() => setScreeningSubTab('corporate-structure')}
                >
                  Corporate Structure
                </button>
                <button
                  className={`screening-sub-tab ${screeningSubTab === 'csl-matches' ? 'active' : ''}`}
                  onClick={() => setScreeningSubTab('csl-matches')}
                >
                  CSL Matches
                </button>
                {(comprehensiveResults?.opensanctions?.selected ||
                  comprehensiveResults?.risk_assessment?.risk_level === 'FLAGS') && (
                  <button
                    className={`screening-sub-tab ${screeningSubTab === 'additional-screening' ? 'active' : ''}`}
                    onClick={() => setScreeningSubTab('additional-screening')}
                  >
                    Additional Screening {opensanctionsBadgeText}
                  </button>
                )}
              </div>

              {/* Corporate Structure sub-tab */}
              {screeningSubTab === 'corporate-structure' && (
                <div className="screening-sub-content">
                  {comprehensiveResults?.corporate_structure ? (
                    <CSLAnnotatedStructure
                      structure={comprehensiveResults.corporate_structure}
                      matchValidations={comprehensiveResults.match_validation?.validations || []}
                      cslMatches={displayResults?.results || []}
                    />
                  ) : comprehensiveResults ? (
                    <CSLAnnotatedStructure
                      structure={{
                        root_entity: displayCompanyName,
                        entities: [{
                          legal_name: displayCompanyName,
                          relationship: 'SELF',
                          country: comprehensiveResults.enrichment?.profile?.country || null,
                          confidence: 'HIGH',
                        }],
                        relationships: [],
                      }}
                      matchValidations={comprehensiveResults.match_validation?.validations || []}
                      cslMatches={displayResults?.results || []}
                    />
                  ) : null}
                </div>
              )}

              {/* CSL Matches sub-tab */}
              {screeningSubTab === 'csl-matches' && displayResults && (
                <div className="screening-sub-content">
                  <div className="csl-info-box">
                    <p><strong>What is the CSL?</strong> The Consolidated Screening List (CSL) is the US government's master list combining 13 export screening lists including the Entity List, Denied Persons List, and SDN list.</p>
                    <p className="info-note">Being on the CSL means export restrictions may apply. Common name matches are analyzed by AI to determine if they refer to the same entity or are coincidental.</p>
                  </div>

              {displayResults.success && displayResults.total === 0 && tabCounts.filtered === 0 && (
                <p className="no-results">No matches found on the Consolidated Screening List.</p>
              )}

              {displayResults.success && (
                <>
                  {/* Filter Tabs - always show for comprehensive results */}
                  {comprehensiveResults && (
                    <div className="csl-filter-tabs">
                      <button
                        className={`filter-tab tab-all ${cslFilterTab === 'all' ? 'active' : ''}`}
                        onClick={() => setCslFilterTab('all')}
                      >
                        All ({tabCounts.all})
                      </button>
                      {comprehensiveResults.match_validation && !comprehensiveResults.match_validation.skipped && (
                        <>
                          <button
                            className={`filter-tab tab-confirmed ${cslFilterTab === 'confirmed' ? 'active' : ''}`}
                            onClick={() => setCslFilterTab('confirmed')}
                            disabled={tabCounts.confirmed === 0}
                          >
                            Confirmed ({tabCounts.confirmed})
                          </button>
                          <button
                            className={`filter-tab tab-uncertain ${cslFilterTab === 'uncertain' ? 'active' : ''}`}
                            onClick={() => setCslFilterTab('uncertain')}
                            disabled={tabCounts.uncertain === 0}
                          >
                            Uncertain ({tabCounts.uncertain})
                          </button>
                          <button
                            className={`filter-tab tab-coincidental ${cslFilterTab === 'coincidental' ? 'active' : ''}`}
                            onClick={() => setCslFilterTab('coincidental')}
                            disabled={tabCounts.coincidental === 0}
                          >
                            Coincidental ({tabCounts.coincidental})
                          </button>
                        </>
                      )}
                      <button
                        className={`filter-tab tab-filtered ${cslFilterTab === 'filtered' ? 'active' : ''}`}
                        onClick={() => setCslFilterTab('filtered')}
                        disabled={tabCounts.filtered === 0}
                      >
                        Filtered ({tabCounts.filtered})
                      </button>
                    </div>
                  )}

                  {!comprehensiveResults && (
                    <p className="results-count">
                      Found {displayResults.total} potential match(es)
                      {tabCounts.filtered > 0 && (
                        <span className="filtered-count">
                          {' '}({tabCounts.filtered} low-confidence matches filtered)
                        </span>
                      )}
                    </p>
                  )}

                  {comprehensiveResults?.match_validation?.skipped && (
                    <div className="validation-summary">
                      <p className="validation-skipped">
                        {comprehensiveResults.match_validation.skip_reason}
                      </p>
                    </div>
                  )}

                  {comprehensiveResults?.match_validation?.partial_validation && (
                    <div className="validation-summary">
                      <p className="validation-skipped">
                        {comprehensiveResults.match_validation.partial_reason}
                      </p>
                    </div>
                  )}

                  {comprehensiveResults?.match_validation && !comprehensiveResults.match_validation.skipped && cslFilterTab !== 'all' && cslFilterTab !== 'filtered' && (
                    <div className="tab-explanation">
                      {cslFilterTab === 'confirmed' && (
                        <p className="has-confirmed">Showing matches confirmed to be the same entity or affiliated with the company being screened.</p>
                      )}
                      {cslFilterTab === 'uncertain' && (
                        <p className="has-uncertain">Showing matches that require manual review to determine relationship.</p>
                      )}
                      {cslFilterTab === 'coincidental' && (
                        <p className="all-coincidental">Showing coincidental name overlaps - not the same entity as the company being screened.</p>
                      )}
                    </div>
                  )}

                  {cslFilterTab === 'filtered' && (
                    <div className="tab-explanation">
                      <p className="filtered-explanation">
                        These matches were automatically filtered due to low match scores. They are shown for completeness but are unlikely to be relevant.
                      </p>
                    </div>
                  )}

                  {/* Results List — accordion rows */}
                  <div className="results-list">
                    {filteredCSLResults.length === 0 ? (
                      <p className="no-results-in-tab">No matches in this category.</p>
                    ) : (
                      <>
                        {filteredCSLResults.length >= 3 && (
                          <button
                            className="expand-all-toggle"
                            onClick={() => {
                              expandedCSLRows.size === filteredCSLResults.length
                                ? setExpandedCSLRows(new Set())
                                : setExpandedCSLRows(new Set(filteredCSLResults.map((_, i) => i)));
                            }}
                          >
                            {expandedCSLRows.size === filteredCSLResults.length ? 'Collapse All' : 'Expand All'}
                          </button>
                        )}
                        {filteredCSLResults.map((result: SearchResult, index: number) => {
                          const originalIndex = displayResults.results?.findIndex(r => r.name === result.name && r.source === result.source) ?? -1;
                          const validation = comprehensiveResults?.match_validation?.validations?.find(
                            v => v.match_index === originalIndex + 1
                          );
                          const relationshipClass = validation?.relationship?.toLowerCase() || '';
                          const isCoincidental = validation?.relationship === 'COINCIDENTAL';
                          const isFiltered = cslFilterTab === 'filtered';
                          const isExpanded = expandedCSLRows.has(index);

                          // Match AI screening_list_analysis by source name
                          const screeningAnalyses = comprehensiveResults?.ai_research?.research?.screening_list_analysis || [];
                          const matchedAnalysis = screeningAnalyses.find(
                            (sa: ScreeningListAnalysis) => {
                              const listName = (sa.list_name || '').toLowerCase();
                              const source = (result.source || '').toLowerCase();
                              return listName.includes(source) || source.includes(listName) ||
                                (listName.includes('entity') && source.includes('entity')) ||
                                (listName.includes('sdn') && source.includes('sdn')) ||
                                (listName.includes('denied') && source.includes('denied')) ||
                                (listName.includes('military') && source.includes('military')) ||
                                (listName.includes('unverified') && source.includes('unverified'));
                            }
                          );

                          return (
                            <div key={index} className={`result-card ${relationshipClass ? `validation-${relationshipClass}` : ''} ${isFiltered ? 'filtered-match' : ''}`}>
                              {/* Accordion summary row */}
                              <div
                                className="result-card-summary"
                                onClick={() => toggleCSLRow(index)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCSLRow(index); } }}
                              >
                                <span className="expand-icon">{isExpanded ? '\u25BE' : '\u25B8'}</span>
                                <span className="result-name">{result.name}</span>
                                {validation ? (
                                  <>
                                    <span className={`relationship-badge ${relationshipClass}`}>
                                      {validation.relationship}
                                    </span>
                                    <span className={`confidence-badge-summary confidence-${validation.confidence.toLowerCase()}`}>
                                      {validation.confidence}
                                    </span>
                                  </>
                                ) : isFiltered ? (
                                  <span className="relationship-badge filtered">LOW SCORE</span>
                                ) : comprehensiveResults?.match_validation ? (
                                  <span className="relationship-badge unknown">PENDING</span>
                                ) : null}
                                {result.effective_score !== undefined && (
                                  <span className="compact-score">{(result.effective_score * 100).toFixed(0)}%</span>
                                )}
                                {result.addresses && result.addresses.length > 0 && result.addresses[0].country && (
                                  <span className="compact-country">{result.addresses[0].country}</span>
                                )}
                                <span className="compact-source">{result.source}</span>
                              </div>

                              {/* Expanded detail panel */}
                              {isExpanded && (
                                <div className="result-card-details">
                                  <p><strong>Source:</strong> {result.source}
                                    <span className="source-hint">
                                      {result.source === 'Entity List' && ' (BIS export restrictions)'}
                                      {result.source === 'Denied Persons List' && ' (export privileges denied)'}
                                      {result.source === 'Unverified List' && ' (end-use verification required)'}
                                      {result.source === 'Military End User List' && ' (military end-use restrictions)'}
                                      {result.source === 'Specially Designated Nationals (SDN)' && ' (OFAC sanctions)'}
                                      {result.source === 'Foreign Sanctions Evaders' && ' (sanctions evasion)'}
                                      {result.source === 'Sectoral Sanctions' && ' (sector-specific restrictions)'}
                                    </span>
                                  </p>
                                  <p><strong>Type:</strong> {result.type}</p>
                                  {result.programs && result.programs.length > 0 && (
                                    <p><strong>Programs:</strong> {result.programs.join(', ')}</p>
                                  )}
                                  {result.addresses && result.addresses.length > 0 && (
                                    <p><strong>Country:</strong> {result.addresses[0].country}</p>
                                  )}
                                  {result.effective_score !== undefined && (
                                    <p><strong>Match Score:</strong> {(result.effective_score * 100).toFixed(0)}%</p>
                                  )}
                                  {result.name_similarity !== undefined && (
                                    <p><strong>Name Similarity:</strong> {(result.name_similarity * 100).toFixed(0)}%</p>
                                  )}
                                  {result.federal_register_notice && (
                                    <p><strong>Federal Register:</strong> {result.federal_register_notice}</p>
                                  )}
                                  {result.license_requirement && (
                                    <p><strong>License Requirement:</strong> {result.license_requirement}</p>
                                  )}
                                  {result.license_policy && (
                                    <p>
                                      <strong>License Policy:</strong>{' '}
                                      <span className={result.license_policy.toLowerCase().includes('denial') ? 'license-policy-denial' : ''}>
                                        {result.license_policy}
                                      </span>
                                    </p>
                                  )}
                                  {result.alt_names && result.alt_names.length > 0 && (
                                    <p><strong>Also Known As:</strong> <span className="alt-names-list">{result.alt_names.join(', ')}</span></p>
                                  )}
                                  {result.remarks && (
                                    <p><strong>Remarks:</strong> {result.remarks.length > 300 ? result.remarks.substring(0, 300) + '...' : result.remarks}</p>
                                  )}
                                  {validation && (
                                    <div className="validation-details">
                                      <p className="validation-reasoning">
                                        <strong>Analysis:</strong> {validation.reasoning}
                                      </p>
                                      <span className={`confidence-badge confidence-${validation.confidence.toLowerCase()}`}>
                                        {validation.confidence} Confidence
                                      </span>
                                      {(validation.confidence === 'LOW' || validation.confidence === 'MEDIUM') && (
                                        <p className="confidence-guidance">{validation.confidence === 'LOW' ? 'Low' : 'Medium'} confidence — manual verification recommended</p>
                                      )}
                                      {isCoincidental && (
                                        <span className="coincidental-note">This is NOT the company being screened</span>
                                      )}
                                      {validation.rechecked && (
                                        <div className="recheck-info">
                                          {isCoincidental ? (
                                            <span className="recheck-label confirmed-unrelated">Verified unrelated (secondary check)</span>
                                          ) : (
                                            <span className="recheck-label reclassified">Reclassified from COINCIDENTAL (secondary check)</span>
                                          )}
                                          {validation.recheck_source_url && (() => {
                                            const href = safeExternalUrl(validation.recheck_source_url);
                                            return href ? (
                                              <a href={href} target="_blank" rel="noopener noreferrer" className="recheck-source-link">
                                                {validation.recheck_source_title || 'View source'}
                                              </a>
                                            ) : null;
                                          })()}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {/* Inline listing analysis from AI research */}
                                  {matchedAnalysis && matchedAnalysis.is_same_entity && matchedAnalysis.listing_reason && (
                                    <div className="listing-details">
                                      <p><strong>Why Listed:</strong> {matchedAnalysis.listing_reason}</p>
                                      {matchedAnalysis.listing_date && <p><strong>Date Listed:</strong> {matchedAnalysis.listing_date}</p>}
                                      {matchedAnalysis.restrictions && <p><strong>Restrictions:</strong> {matchedAnalysis.restrictions}</p>}
                                      {matchedAnalysis.federal_register_url && (() => {
                                        const href = safeExternalUrl(matchedAnalysis.federal_register_url);
                                        return href ? (
                                          <p className="federal-register-link">
                                            <a href={href} target="_blank" rel="noopener noreferrer">
                                              View Federal Register Notice
                                            </a>
                                          </p>
                                        ) : null;
                                      })()}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                </>
              )}
                </div>
              )}

              {/* Additional Screening sub-tab (OpenSanctions) */}
              {screeningSubTab === 'additional-screening' && (
                <div className="screening-sub-content">
                  {comprehensiveResults?.opensanctions && comprehensiveResults.opensanctions.selected ? (
                    comprehensiveResults.opensanctions.success ? (
                      <div className="opensanctions-content">
                        <div className="opensanctions-info-box">
                          <p><strong>What is OpenSanctions?</strong> A comprehensive database that aggregates sanctions lists, watchlists, PEP databases, and corporate registries from multiple jurisdictions (US, EU, UK, UN, etc.).</p>
                          <p className="info-note">Note: Appearing in this database does NOT automatically mean an entity is sanctioned. Many entries are reference data from corporate registries or PEP (Politically Exposed Person) databases.</p>
                        </div>

                        <div className={`opensanctions-summary risk-${riskClass(comprehensiveResults.opensanctions.risk_level || 'no findings')}`}>
                          <p className="summary-text">{comprehensiveResults.opensanctions.summary}</p>
                          {comprehensiveResults.opensanctions.list_sources && comprehensiveResults.opensanctions.list_sources.length > 0 && (
                            <p className="list-sources">
                              <strong>Jurisdictions searched:</strong> {comprehensiveResults.opensanctions.list_sources.join(', ')}
                            </p>
                          )}
                        </div>

                        <div className="opensanctions-stats">
                          <span className="stat">
                            <strong>{comprehensiveResults.opensanctions.total || 0}</strong> total matches
                          </span>
                          {(comprehensiveResults.opensanctions.sanctioned_count || 0) > 0 && (
                            <span className="stat sanctioned">
                              <strong>{comprehensiveResults.opensanctions.sanctioned_count}</strong> on sanctions lists
                            </span>
                          )}
                          {(comprehensiveResults.opensanctions.informational_count || 0) > 0 && (
                            <span className="stat informational">
                              <strong>{comprehensiveResults.opensanctions.informational_count}</strong> reference only
                            </span>
                          )}
                        </div>

                        {comprehensiveResults.opensanctions.results && comprehensiveResults.opensanctions.results.length > 0 && (
                          <div className="opensanctions-results">
                            <h4>Matches Found:</h4>
                            {comprehensiveResults.opensanctions.results.map((result: any, idx: number) => (
                              <div key={idx} className={`opensanctions-match category-${(result.match_category || 'unknown').toLowerCase()}`}>
                                <div className="match-header">
                                  <span className="match-name">{result.caption}</span>
                                  <span className="match-score">{((result.score || 0) * 100).toFixed(0)}% match</span>
                                  <span className={`category-badge ${(result.match_category || 'unknown').toLowerCase()}`}>
                                    {result.match_category === 'SANCTIONED' ? 'SANCTIONED' :
                                     result.match_category === 'HIGH_RISK' ? 'HIGH RISK' :
                                     result.match_category === 'MEDIUM_RISK' ? 'WATCHLIST' :
                                     result.match_category === 'LOW_RISK' ? 'PEP' :
                                     'REFERENCE ONLY'}
                                  </span>
                                </div>
                                {result.match_explanation && (
                                  <p className="match-explanation">{result.match_explanation}</p>
                                )}
                                <div className="match-details">
                                  <p><strong>Type:</strong> {result.schema}</p>
                                  {result.countries && result.countries.length > 0 && (
                                    <p><strong>Countries:</strong> {result.countries.join(', ')}</p>
                                  )}
                                  {result.topics && result.topics.length > 0 && (
                                    <p><strong>Categories:</strong> {result.topics.join(', ')}</p>
                                  )}
                                  {result.list_sources && result.list_sources.length > 0 && (
                                    <p><strong>Jurisdictions:</strong> {result.list_sources.join(', ')}</p>
                                  )}
                                  {result.datasets && result.datasets.length > 0 && (
                                    <p className="datasets"><strong>Sources:</strong> {result.datasets.slice(0, 3).join(', ')}{result.datasets.length > 3 ? ` (+${result.datasets.length - 3} more)` : ''}</p>
                                  )}
                                  {result.entity_url && (
                                    <p className="entity-link">
                                      <a href={result.entity_url} target="_blank" rel="noopener noreferrer" className="opensanctions-entity-link">
                                        View on OpenSanctions
                                      </a>
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {comprehensiveResults.opensanctions.total === 0 && (
                          <p className="no-matches">No matches found in OpenSanctions database.</p>
                        )}
                      </div>
                    ) : (
                      <div className="opensanctions-error">
                        <p>OpenSanctions screening failed: {comprehensiveResults.opensanctions.error}</p>
                      </div>
                    )
                  ) : (
                    <div className="opensanctions-on-demand">
                      {opensanctionsLoading ? (
                        <div className="opensanctions-loading">
                          <div className="progress-spinner"></div>
                          <span>Screening against OpenSanctions...</span>
                        </div>
                      ) : opensanctionsError ? (
                        <div className="opensanctions-error-offer">
                          <p>{opensanctionsError}</p>
                          <button className="primary" onClick={runOpenSanctionsCheck}>Retry</button>
                        </div>
                      ) : (
                        <div className="opensanctions-offer">
                          <p>Screen against international sanctions lists (EU, UK, UN, and others) for broader coverage beyond US CSL.</p>
                          <button className="primary" onClick={runOpenSanctionsCheck}>Run OpenSanctions Check</button>
                          <p className="hint">Screens up to 10 entity names against the OpenSanctions database. This may take 10-15 seconds.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* --- Tier 2: Supporting Evidence --- */}
      {comprehensiveResults && (
        <div className="tier-divider">
          <h3>Supporting Evidence</h3>
        </div>
      )}

      {/* 3. Company Intelligence (merged Company Profile + AI Research, minus screening_list_analysis) */}
      {comprehensiveResults && (comprehensiveResults.enrichment?.is_enriched || comprehensiveResults.ai_research?.success) && (
        <div id="company-intelligence-section" className="results-section ai-section tier-2">
          <div
            className="section-header collapsible"
            onClick={() => toggleSection('companyIntelligence')}
          >
            <h2>
              Company Intelligence
              {companyIntelBadgeText && (
                <span className="section-badge badge-neutral">{companyIntelBadgeText}</span>
              )}
            </h2>
            <span className="toggle-icon">{sectionsExpanded.companyIntelligence ? '−' : '+'}</span>
          </div>

          {sectionsExpanded.companyIntelligence && (
            <div className="section-content">
              {/* Company Profile table */}
              {comprehensiveResults.enrichment?.profile && comprehensiveResults.enrichment.is_enriched && (
                <div className="ai-subsection company-profile-inline">
                  <h4>Company Profile</h4>
                  <div className="profile-table">
                    {comprehensiveResults.enrichment.profile.legal_name && (
                      <div className="profile-row">
                        <span className="profile-label">Legal Name</span>
                        <span className="profile-value">{comprehensiveResults.enrichment.profile.legal_name}</span>
                      </div>
                    )}
                    {comprehensiveResults.enrichment.profile.trade_names && comprehensiveResults.enrichment.profile.trade_names.length > 0 && (
                      <div className="profile-row">
                        <span className="profile-label">Trade Names</span>
                        <span className="profile-value">{comprehensiveResults.enrichment.profile.trade_names.join(', ')}</span>
                      </div>
                    )}
                    {(comprehensiveResults.enrichment.profile.full_address || comprehensiveResults.enrichment.profile.address) && (
                      <div className="profile-row">
                        <span className="profile-label">Address</span>
                        <span className="profile-value">{comprehensiveResults.enrichment.profile.full_address || comprehensiveResults.enrichment.profile.address}</span>
                      </div>
                    )}
                    {comprehensiveResults.enrichment.profile.country && (
                      <div className="profile-row">
                        <span className="profile-label">Country</span>
                        <span className="profile-value">{comprehensiveResults.enrichment.profile.country}</span>
                      </div>
                    )}
                    {comprehensiveResults.enrichment.profile.industry && (
                      <div className="profile-row">
                        <span className="profile-label">Industry</span>
                        <span className="profile-value">{comprehensiveResults.enrichment.profile.industry}</span>
                      </div>
                    )}
                    {comprehensiveResults.enrichment.profile.parent_company && (
                      <div className="profile-row">
                        <span className="profile-label">Parent Company</span>
                        <span className="profile-value">{comprehensiveResults.enrichment.profile.parent_company}</span>
                      </div>
                    )}
                    {comprehensiveResults.enrichment.profile.website && (
                      <div className="profile-row">
                        <span className="profile-label">Website</span>
                        <span className="profile-value">
                          {safeExternalUrl(comprehensiveResults.enrichment.profile.website) ? (
                            <a href={safeExternalUrl(comprehensiveResults.enrichment.profile.website)!} target="_blank" rel="noopener noreferrer">
                              {comprehensiveResults.enrichment.profile.website}
                            </a>
                          ) : (
                            comprehensiveResults.enrichment.profile.website
                          )}
                        </span>
                      </div>
                    )}
                    {comprehensiveResults.enrichment.profile.description && (
                      <div className="profile-row description-row">
                        <span className="profile-label">Description</span>
                        <span className="profile-value">{comprehensiveResults.enrichment.profile.description}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {comprehensiveResults.ai_research?.success && comprehensiveResults.ai_research.research && (
                <div className="ai-research-content">
                  {/* PRC Verification */}
                  {comprehensiveResults.ai_research.research.prc_verification && (
                    <div className="ai-subsection prc-verification">
                      <h4>PRC Verification</h4>
                      <div className="prc-details">
                        {comprehensiveResults.ai_research.research.prc_verification.mainland_registration && (
                          <div className="prc-item">
                            <strong>Mainland China Registration:</strong>
                            <span className={comprehensiveResults.ai_research.research.prc_verification.mainland_registration.verified ? 'verified' : 'not-verified'}>
                              {comprehensiveResults.ai_research.research.prc_verification.mainland_registration.verified ? ' Verified' : ' Not Found'}
                            </span>
                            {comprehensiveResults.ai_research.research.prc_verification.mainland_registration.registration_type && (
                              <p>Type: {comprehensiveResults.ai_research.research.prc_verification.mainland_registration.registration_type}</p>
                            )}
                            {comprehensiveResults.ai_research.research.prc_verification.mainland_registration.registration_number && (
                              <p>Registration #: {comprehensiveResults.ai_research.research.prc_verification.mainland_registration.registration_number}</p>
                            )}
                          </div>
                        )}
                        {comprehensiveResults.ai_research.research.prc_verification.hong_kong_entity?.exists && (
                          <div className="prc-item hk-note">
                            <strong>Hong Kong Entity:</strong> Found
                            {comprehensiveResults.ai_research.research.prc_verification.hong_kong_entity.company_number && (
                              <span> (#{comprehensiveResults.ai_research.research.prc_verification.hong_kong_entity.company_number})</span>
                            )}
                            <p className="hk-warning">Note: HK registration is separate from Mainland PRC registration</p>
                          </div>
                        )}
                        {comprehensiveResults.ai_research.research.prc_verification.state_ownership?.is_soe && (
                          <div className="prc-item soe-warning">
                            <strong>State-Owned Enterprise:</strong> Yes
                            {comprehensiveResults.ai_research.research.prc_verification.state_ownership.evidence && (
                              <p>{comprehensiveResults.ai_research.research.prc_verification.state_ownership.evidence}</p>
                            )}
                          </div>
                        )}
                        {comprehensiveResults.ai_research.research.prc_verification.military_connections?.has_connections && (
                          <div className="prc-item military-warning">
                            <strong>Military Connections:</strong> Yes
                            {comprehensiveResults.ai_research.research.prc_verification.military_connections.details && (
                              <p>{comprehensiveResults.ai_research.research.prc_verification.military_connections.details}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Legacy PRC Registration Status */}
                  {!comprehensiveResults.ai_research.research.prc_verification &&
                   comprehensiveResults.ai_research.research.prc_registration_status && (
                    <div className="ai-subsection prc-status">
                      <h4>PRC Registration Status</h4>
                      <div className="prc-details">
                        <p>
                          <strong>Legitimate PRC Registration:</strong>{' '}
                          {comprehensiveResults.ai_research.research.prc_registration_status.has_legitimate_registration === true
                            ? 'Yes'
                            : comprehensiveResults.ai_research.research.prc_registration_status.has_legitimate_registration === false
                              ? 'Not Found'
                              : 'Unknown'}
                        </p>
                        {comprehensiveResults.ai_research.research.prc_registration_status.registration_details && (
                          <p><strong>Details:</strong> {comprehensiveResults.ai_research.research.prc_registration_status.registration_details}</p>
                        )}
                        {comprehensiveResults.ai_research.research.prc_registration_status.state_owned_enterprise === true && (
                          <p className="soe-warning"><strong>State-Owned Enterprise:</strong> Yes</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Risk Indicators */}
                  {comprehensiveResults.ai_research.research.risk_indicators &&
                   comprehensiveResults.ai_research.research.risk_indicators.length > 0 && (
                    <div className="ai-subsection risk-indicators">
                      <h4>Risk Indicators</h4>
                      <ul className="risk-list">
                        {comprehensiveResults.ai_research.research.risk_indicators.map((indicator: string, idx: number) => (
                          <li key={idx}>{indicator}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* What Public Sources Confirm */}
                  {comprehensiveResults.ai_research.research.public_sources_confirm &&
                   comprehensiveResults.ai_research.research.public_sources_confirm.length > 0 && (
                    <div className="ai-subsection confirmed">
                      <h4>What Public Sources Confirm</h4>
                      <ul className="confirmed-list">
                        {comprehensiveResults.ai_research.research.public_sources_confirm.map((item: string, idx: number) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* What This Doesn't Confirm Yet */}
                  {comprehensiveResults.ai_research.research.public_sources_do_not_confirm &&
                   comprehensiveResults.ai_research.research.public_sources_do_not_confirm.length > 0 && (
                    <div className="ai-subsection not-confirmed">
                      <h4>What This Doesn't Confirm Yet</h4>
                      <ul className="not-confirmed-list">
                        {comprehensiveResults.ai_research.research.public_sources_do_not_confirm.map((item: string, idx: number) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Legacy fields */}
                  {!comprehensiveResults.ai_research.research.public_sources_confirm &&
                   comprehensiveResults.ai_research.research.export_compliance_concerns &&
                   comprehensiveResults.ai_research.research.export_compliance_concerns.length > 0 && (
                    <div className="ai-subsection compliance-concerns">
                      <h4>Export Compliance Concerns</h4>
                      <ul>
                        {comprehensiveResults.ai_research.research.export_compliance_concerns.map((concern: string, idx: number) => (
                          <li key={idx}>{concern}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {!comprehensiveResults.ai_research.research.public_sources_confirm &&
                   comprehensiveResults.ai_research.research.government_military_affiliations &&
                   comprehensiveResults.ai_research.research.government_military_affiliations.length > 0 && (
                    <div className="ai-subsection military-affiliations">
                      <h4>Government/Military Affiliations</h4>
                      <ul>
                        {comprehensiveResults.ai_research.research.government_military_affiliations.map((affiliation: string, idx: number) => (
                          <li key={idx}>{affiliation}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* References - deep-linked source URLs */}
                  {(() => {
                    const groundingUrls = comprehensiveResults.ai_research!.research!.grounding_source_urls || [];
                    const keySources = comprehensiveResults.ai_research!.research!.key_sources || [];
                    const enrichmentUrls = comprehensiveResults.enrichment?.profile?.enrichment_source_urls || [];

                    // Aggregate all source URLs, deduplicate by URL
                    const seenUrls = new Set<string>();
                    const allRefs: Array<{ title: string; url: string }> = [];

                    for (const src of groundingUrls) {
                      if (src.url && !seenUrls.has(src.url)) {
                        seenUrls.add(src.url);
                        allRefs.push({ title: src.title || src.url, url: src.url });
                      }
                    }
                    for (const src of keySources) {
                      if (typeof src !== 'string' && src.url && !seenUrls.has(src.url)) {
                        seenUrls.add(src.url);
                        allRefs.push({ title: src.name || src.url, url: src.url });
                      }
                    }
                    for (const src of enrichmentUrls) {
                      if (src.url && !seenUrls.has(src.url)) {
                        seenUrls.add(src.url);
                        allRefs.push({ title: src.title || src.url, url: src.url });
                      }
                    }

                    if (allRefs.length === 0) return null;

                    return (
                      <div className="ai-references">
                        <h4>References</h4>
                        <ul className="references-list">
                          {allRefs.map((ref, idx) => {
                            const srcUrl = safeExternalUrl(ref.url);
                            return (
                              <li key={idx}>
                                {srcUrl ? (
                                  <a href={srcUrl} target="_blank" rel="noopener noreferrer" className="reference-link">
                                    {ref.title}
                                  </a>
                                ) : (
                                  <span>{ref.title}</span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })()}

                  <div className="ai-metadata">
                    <span className={`confidence confidence-${comprehensiveResults.ai_research.research.research_confidence?.toLowerCase()}`}>
                      Confidence: {comprehensiveResults.ai_research.research.research_confidence || 'Unknown'}
                    </span>
                    <span className="ai-provider">
                      Provider: {comprehensiveResults.ai_research.research.ai_provider || 'Unknown'}
                    </span>
                  </div>
                </div>
              )}

              {/* AI Research Error */}
              {comprehensiveResults.ai_research && !comprehensiveResults.ai_research.success && (
                <p className="ai-error-message">AI research unavailable: {comprehensiveResults.ai_research.error}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Corporate Structure (standalone section) */}
      {comprehensiveResults?.corporate_structure && comprehensiveResults.corporate_structure.entities.length > 0 && (
        <div id="corporate-structure-section" className="results-section tier-2">
          <div
            className="section-header collapsible"
            onClick={() => toggleSection('corporateStructure')}
          >
            <h2>
              Corporate Structure
              <span className="section-badge badge-neutral">
                {comprehensiveResults.corporate_structure.entities.length} {comprehensiveResults.corporate_structure.entities.length === 1 ? 'entity' : 'entities'}
              </span>
            </h2>
            <span className="toggle-icon">{sectionsExpanded.corporateStructure ? '−' : '+'}</span>
          </div>

          {sectionsExpanded.corporateStructure && (
            <div className="section-content">
              <CorporateStructureSection
                structure={comprehensiveResults.corporate_structure}
                matchValidations={comprehensiveResults.match_validation?.validations || []}
                cslMatches={displayResults?.results || []}
              />
            </div>
          )}
        </div>
      )}

      {/* Domain Intelligence */}
      {comprehensiveResults && (
        <div id="domain-section" className="results-section domain-section tier-2">
          <div
            className="section-header collapsible"
            onClick={() => toggleSection('domain')}
          >
            <h2>
              Domain Intelligence
              {domainBadgeText && (
                <span className={`section-badge ${(comprehensiveResults.domain_results?.risk_flags?.length || 0) > 0 ? 'badge-alert' : 'badge-neutral'}`}>
                  {domainBadgeText}
                </span>
              )}
            </h2>
            <span className="toggle-icon">{sectionsExpanded.domain ? '−' : '+'}</span>
          </div>

          {sectionsExpanded.domain && (
            <div className="section-content">
              {comprehensiveResults.domain_results?.domains && comprehensiveResults.domain_results.domains.length > 0 ? (
                /* Multi-domain rendering */
                <div className="domain-info multi-domain">
                  {comprehensiveResults.domain_results.total_checked && comprehensiveResults.domain_results.total_checked > 1 && (
                    <p className="domain-count">{comprehensiveResults.domain_results.total_checked} domain(s) checked</p>
                  )}
                  {comprehensiveResults.domain_results.domains.map((dr: SingleDomainResult, idx: number) => (
                    <div key={idx} className={`domain-card ${dr.is_primary ? 'domain-primary' : 'domain-discovered'}`}>
                      <div className="domain-card-header">
                        <strong>
                          {dr.domain && safeExternalUrl(dr.domain) ? (
                            <a href={safeExternalUrl(dr.domain)!} target="_blank" rel="noopener noreferrer" className="domain-link">
                              {dr.domain}
                            </a>
                          ) : (dr.domain || 'Unknown')}
                        </strong>
                        <span className={`domain-badge ${dr.is_primary ? 'badge-primary' : 'badge-discovered'}`}>
                          {dr.is_primary ? 'PRIMARY' : 'DISCOVERED'}
                        </span>
                      </div>
                      {dr.success ? (
                        <div className="domain-overview">
                          {dr.registrar && <p><strong>Registrar:</strong> {dr.registrar}</p>}
                          {dr.create_date && <p><strong>Created:</strong> {dr.create_date}</p>}
                          {dr.domain_age_days != null && (
                            <p><strong>Domain Age:</strong> {Math.floor(dr.domain_age_days / 365)} years, {dr.domain_age_days % 365} days</p>
                          )}
                          {dr.registrant_country && <p><strong>Registrant Country:</strong> {dr.registrant_country}</p>}
                          {dr.risk_flags && dr.risk_flags.length > 0 && (
                            <div className="domain-risks">
                              <ul>
                                {dr.risk_flags.map((flag: string, fi: number) => (
                                  <li key={fi} className="risk-indicator">{flag}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="domain-error-block">
                          <p className="domain-error">{dr.error || 'Unable to retrieve WHOIS information.'}</p>
                          <button
                            className="retry-btn"
                            disabled={domainRetryLoading.has(dr.domain || '')}
                            onClick={() => dr.domain && retryDomainCheck(dr.domain)}
                          >
                            {domainRetryLoading.has(dr.domain || '') ? 'Retrying...' : 'Retry WHOIS Lookup'}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : comprehensiveResults.domain_results?.success ? (
                /* Single-domain fallback (old data) */
                <div className="domain-info">
                  <div className="domain-overview">
                    <p><strong>Domain:</strong> {comprehensiveResults.domain_results.domain && safeExternalUrl(comprehensiveResults.domain_results.domain) ? (
                      <a href={safeExternalUrl(comprehensiveResults.domain_results.domain)!} target="_blank" rel="noopener noreferrer" className="domain-link">
                        {comprehensiveResults.domain_results.domain}
                      </a>
                    ) : (comprehensiveResults.domain_results.domain || 'Unknown')}</p>
                    {comprehensiveResults.domain_results.registrar && (
                      <p><strong>Registrar:</strong> {comprehensiveResults.domain_results.registrar}</p>
                    )}
                    {comprehensiveResults.domain_results.create_date && (
                      <p><strong>Created:</strong> {comprehensiveResults.domain_results.create_date}</p>
                    )}
                    {comprehensiveResults.domain_results.domain_age_days != null && (
                      <p><strong>Domain Age:</strong> {Math.floor(comprehensiveResults.domain_results.domain_age_days / 365)} years, {comprehensiveResults.domain_results.domain_age_days % 365} days</p>
                    )}
                    {comprehensiveResults.domain_results.registrant_country && (
                      <p><strong>Registrant Country:</strong> {comprehensiveResults.domain_results.registrant_country}</p>
                    )}
                  </div>
                  {comprehensiveResults.domain_results.risk_flags && comprehensiveResults.domain_results.risk_flags.length > 0 && (
                    <div className="domain-risks">
                      <h4>Risk Indicators</h4>
                      <ul>
                        {comprehensiveResults.domain_results.risk_flags.map((indicator: string, idx: number) => (
                          <li key={idx} className="risk-indicator">{indicator}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : comprehensiveResults.domain_results?.error ? (
                <div className="domain-error-block">
                  <p className="domain-error">{comprehensiveResults.domain_results.error}</p>
                  <button
                    className="retry-btn"
                    disabled={domainRetryLoading.has(comprehensiveResults.domain_results.domain || '')}
                    onClick={() => comprehensiveResults.domain_results?.domain && retryDomainCheck(comprehensiveResults.domain_results.domain)}
                  >
                    {domainRetryLoading.has(comprehensiveResults.domain_results.domain || '') ? 'Retrying...' : 'Retry WHOIS Lookup'}
                  </button>
                </div>
              ) : !comprehensiveResults.domain_results ? (
                <p className="no-domain">No domain was provided or discovered during enrichment. Enter a domain in the search form to get WHOIS registration data.</p>
              ) : (
                <p className="no-domain">No domain information available.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* 6. Methodology & Sources */}
      {comprehensiveResults && (
        <div id="methodology-section" className="results-section methodology-section tier-2">
          <div
            className="section-header collapsible"
            onClick={() => toggleSection('methodology')}
          >
            <h2>Methodology &amp; Sources</h2>
            <span className="toggle-icon">{sectionsExpanded.methodology ? '−' : '+'}</span>
          </div>

          {sectionsExpanded.methodology && (
            <div className="section-content">
              <p className="methodology-intro">This screening was conducted using the following data sources and methods:</p>
              <ul className="methodology-list">
                {comprehensiveResults.enrichment && (
                  <li><strong>Company Enrichment</strong> &mdash; AI-powered entity resolution using Google Search grounding to identify and verify company details</li>
                )}
                <li><strong>Consolidated Screening List (CSL)</strong> &mdash; US government's combined export screening lists including the Entity List, SDN List, Denied Persons List, and others</li>
                {comprehensiveResults.match_validation && !comprehensiveResults.match_validation.skipped && (
                  <li><strong>AI Match Validation</strong> &mdash; Automated analysis of CSL matches to determine entity relationship (confirmed, coincidental, or uncertain)</li>
                )}
                {comprehensiveResults.domain_results?.success && (
                  <li><strong>Domain Intelligence (WHOIS)</strong> &mdash; Domain registration data including registrar, creation date, and registrant country</li>
                )}
                {comprehensiveResults.ai_research?.success && (
                  <li><strong>AI Research Analysis</strong> &mdash; Comprehensive research using {comprehensiveResults.ai_research.research?.ai_provider || 'AI'} with web search grounding for export compliance risk factors</li>
                )}
                {comprehensiveResults.opensanctions?.selected && (
                  <li><strong>OpenSanctions</strong> &mdash; International sanctions database covering EU, UK, UN, and other jurisdictions</li>
                )}
                {comprehensiveResults.screened_entities && comprehensiveResults.screened_entities.length > 1 && (
                  <li><strong>Multi-Entity Screening</strong> &mdash; The following entities were screened together: {comprehensiveResults.screened_entities.map(e => e.legal_name + (e.country ? ` (${e.country})` : '')).join(', ')}</li>
                )}
                {comprehensiveResults.excluded_companies && comprehensiveResults.excluded_companies.length > 0 && (
                  <li><strong>Excluded Entities</strong> &mdash; The following entities were excluded from screening by the user: {comprehensiveResults.excluded_companies.map(e => e.legal_name + (e.country ? ` (${e.country})` : '')).join(', ')}</li>
                )}
              </ul>

              {/* Aggregated deep-linked references */}
              {(() => {
                const seenUrls = new Set<string>();
                const allRefs: Array<{ title: string; url: string }> = [];

                // Executive brief cited sources
                const briefSources = comprehensiveResults.executive_brief?.cited_sources || [];
                for (const src of briefSources) {
                  if (src.url && !seenUrls.has(src.url)) {
                    seenUrls.add(src.url);
                    allRefs.push({ title: src.label, url: src.url });
                  }
                }

                // AI research grounding URLs
                const groundingUrls = comprehensiveResults.ai_research?.research?.grounding_source_urls || [];
                for (const src of groundingUrls) {
                  if (src.url && !seenUrls.has(src.url)) {
                    seenUrls.add(src.url);
                    allRefs.push({ title: src.title || src.url, url: src.url });
                  }
                }

                // AI research key sources
                const keySources = comprehensiveResults.ai_research?.research?.key_sources || [];
                for (const src of keySources) {
                  if (typeof src !== 'string' && src.url && !seenUrls.has(src.url)) {
                    seenUrls.add(src.url);
                    allRefs.push({ title: src.name || src.url, url: src.url });
                  }
                }

                // Enrichment source URLs
                const enrichmentUrls = comprehensiveResults.enrichment?.profile?.enrichment_source_urls || [];
                for (const src of enrichmentUrls) {
                  if (src.url && !seenUrls.has(src.url)) {
                    seenUrls.add(src.url);
                    allRefs.push({ title: src.title || src.url, url: src.url });
                  }
                }

                if (allRefs.length === 0) return null;

                return (
                  <div className="methodology-references">
                    <h4>References</h4>
                    <ol className="methodology-references-list">
                      {allRefs.map((ref, idx) => {
                        const srcUrl = safeExternalUrl(ref.url);
                        return (
                          <li key={idx}>
                            {srcUrl ? (
                              <a href={srcUrl} target="_blank" rel="noopener noreferrer" className="reference-link">
                                {ref.title}
                              </a>
                            ) : (
                              <span>{ref.title}</span>
                            )}
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Token Usage Display (admin only) */}
      {isAdmin && comprehensiveResults?.token_usage && comprehensiveResults.token_usage.total_tokens > 0 && (
        <div className="token-usage-section">
          <div className="token-usage-content">
            <span className="token-label">AI Tokens Used:</span>
            <span className="token-value">{comprehensiveResults.token_usage.total_tokens.toLocaleString()}</span>
            <span className="token-breakdown">
              ({comprehensiveResults.token_usage.input_tokens.toLocaleString()} in / {comprehensiveResults.token_usage.output_tokens.toLocaleString()} out)
            </span>
            {comprehensiveResults.token_usage.estimated_cost_usd !== undefined && comprehensiveResults.token_usage.estimated_cost_usd > 0 && (
              <span className="token-cost">
                Est. cost: ${comprehensiveResults.token_usage.estimated_cost_usd.toFixed(4)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Process Transparency (admin only) */}
      {isAdmin && transparencyLog.length > 0 && (
        <div className="transparency-section">
          <h3 className="transparency-header">Process Transparency</h3>
          <p className="transparency-description">Every step in the screening pipeline — API calls, AI prompts, and user decisions.</p>

          {transparencyLog.map((entry) => {
            const isExpanded = expandedSteps.has(entry.step);
            return (
              <div key={entry.step} className="transparency-step">
                <button
                  className="step-toggle"
                  onClick={() => setExpandedSteps(prev => {
                    const next = new Set(prev);
                    next.has(entry.step) ? next.delete(entry.step) : next.add(entry.step);
                    return next;
                  })}
                >
                  <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>&#9654;</span>
                  <span className="step-number">{entry.step}</span>
                  <span className={`step-type-badge type-${entry.type}`}>{entry.type}</span>
                  <span className="step-label">{entry.label}</span>
                  {entry.model && <span className="step-model">{entry.model}</span>}
                </button>
                {isExpanded && (
                  <div className="step-content">
                    {entry.endpoint && (
                      <div className="step-detail-row">
                        <span className="step-detail-label">Endpoint</span>
                        <span className="step-detail-value">{entry.endpoint}</span>
                      </div>
                    )}
                    {entry.requestSummary && (
                      <div className="step-detail-row">
                        <span className="step-detail-label">Request</span>
                        <pre className="step-api-content">{entry.requestSummary}</pre>
                      </div>
                    )}
                    {entry.responseSummary && (
                      <div className="step-detail-row">
                        <span className="step-detail-label">Response</span>
                        <span className="step-detail-value">{entry.responseSummary}</span>
                      </div>
                    )}
                    {entry.prompt && (
                      <div className="step-detail-row">
                        <span className="step-detail-label">Full Prompt</span>
                        <pre className="step-prompt-content">{entry.prompt}</pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Back to Top Button */}
      {showBackToTop && (
        <button className="back-to-top" onClick={scrollToTop} aria-label="Back to top">
          ↑
        </button>
      )}
    </div>
  );
};

export default SearchPage;
